// LogCatchView.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import { reverseGeocodeServer } from '../services/geocodeClient';

import { FEATURE } from '../constants/features';
import { getRoddoTheme, getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { FishIQActivityClue, FishIQObservationType, FishScanResult, LogEntry, WeatherData, UserProfile, View, ToastType, TournamentLiveContext, TournamentCaptureSource } from '../types';

import { CameraIcon, CrosshairIcon, MapPinIcon } from './icons';
import RoddoInsightCard from './RoddoInsightCard';
import RoddoConfirmDialog from './RoddoConfirmDialog';
import CatchLocationMapPreview from './CatchLocationMapPreview';
import RoddoPillTabs from './RoddoPillTabs';

import { db, auth } from '../lib/firebase';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { uploadImage, uploadScanImage } from '../services/imageUpload';
import { createTournamentSubmission } from '../services/tournaments';
import { createCommunityPost } from '../services/community';
import { trackRoddoEvent } from '../services/analytics';
import { compressImageFile } from '../utils/imageCompress';
import { analyzeFishPhoto } from '../services/ai';
import { subscribeToEquippedGear, subscribeToGear } from '../services/gear';
import { triggerHabitLoopNotifications } from '../services/habitLoopNotification';
import { completeDailyTaskAction, type DailyTaskActionId } from '../services/dailyRewards';
import {
  FISHIQ_DATA_PROMISE_VERSION,
  FISHIQ_RESEARCH_OBSERVATION_VERSION,
} from '../legal/legalText';

declare const L: any;

const FISHIQ_ACTIVITY_CLUE_OPTIONS: Array<{ value: FishIQActivityClue; label: string }> = [
  { value: 'baitfish_seen', label: 'Baitfish' },
  { value: 'birds_working', label: 'Birds working' },
  { value: 'surface_boils', label: 'Surface boils' },
  { value: 'schooling_seen', label: 'Schooling' },
  { value: 'fish_followed_lure', label: 'Follows' },
  { value: 'short_strikes', label: 'Short strikes' },
  { value: 'missed_bites', label: 'Missed bites' },
  { value: 'no_visible_activity', label: 'No visible activity' },
];

const toSafeCount = (value: string): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(99, Math.round(n))) : null;
};

const roddoTactilePolishStyles = `
  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a,
  [data-roddo-tactile] label[for],
  [data-roddo-tactile] select,
  [data-roddo-tactile] input,
  [data-roddo-tactile] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, filter;
  }

  [data-roddo-tactile] button:hover,
  [data-roddo-tactile] [role="button"]:hover,
  [data-roddo-tactile] a:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.035) saturate(1.035);
  }

  [data-roddo-tactile] button:active,
  [data-roddo-tactile] [role="button"]:active,
  [data-roddo-tactile] a:active {
    transform: translateY(1px) scale(0.985) translateZ(0);
    filter: brightness(0.985) saturate(0.98);
  }

  [data-roddo-tactile] button:focus-visible,
  [data-roddo-tactile] [role="button"]:focus-visible,
  [data-roddo-tactile] a:focus-visible,
  [data-roddo-tactile] input:focus-visible,
  [data-roddo-tactile] textarea:focus-visible,
  [data-roddo-tactile] select:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--roddo-accent-plum-pop, #9278EC) 24%, transparent),
      0 0 20px color-mix(in srgb, var(--roddo-accent-plum-pop, #7657E8) 18%, transparent);
  }

  [data-roddo-tactile] .roddo-polish-card,
  [data-roddo-tactile] article {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-card:hover,
  [data-roddo-tactile] article:hover {
    transform: translateY(-1px);
    filter: saturate(1.025) brightness(1.015);
  }

  [data-roddo-tactile] .roddo-polish-energy {
    position: relative;
    isolation: isolate;
    overflow: hidden;
  }

  [data-roddo-tactile] .roddo-polish-energy::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    pointer-events: none;
    opacity: 0.72;
    background: linear-gradient(90deg,
      transparent,
      rgba(138,112,242,0.30),
      rgba(103,216,232,0.18),
      transparent
    );
    transform: translateX(-18%);
    transition: opacity 220ms ease, transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-energy:hover::before {
    opacity: 0.95;
    transform: translateX(0%);
  }

  [data-roddo-theme="light"] [data-roddo-tactile] .roddo-polish-energy::before,
  [data-roddo-tactile][data-roddo-theme="light"] .roddo-polish-energy::before {
    opacity: 0.50;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-roddo-tactile] button,
    [data-roddo-tactile] [role="button"],
    [data-roddo-tactile] a,
    [data-roddo-tactile] .roddo-polish-card,
    [data-roddo-tactile] article {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;


const COMMUNITY_BAY_STORAGE_KEY = 'roddo.community.selectedBay.v1';
type CommunityBayId = 'global' | 'north-america' | 'south-america' | 'europe' | 'asia' | 'oceania' | 'africa';

const COMMUNITY_BAY_LABELS: Record<CommunityBayId, string> = {
  global: 'Global Bay',
  'north-america': 'North America Bay',
  'south-america': 'South America Bay',
  europe: 'Europe Bay',
  asia: 'Asia Bay',
  oceania: 'Oceania Bay',
  africa: 'Africa Bay',
};

function normalizeCommunityBayId(value: any): CommunityBayId {
  const id = String(value || '').trim().toLowerCase();
  if (id === 'north-america' || id === 'south-america' || id === 'europe' || id === 'asia' || id === 'oceania' || id === 'africa') return id;
  return 'global';
}

function inferDefaultCommunityBayId(): CommunityBayId {
  try {
    const stored = window.localStorage.getItem(COMMUNITY_BAY_STORAGE_KEY);
    if (stored) return normalizeCommunityBayId(stored);
  } catch {}

  try {
    const tz = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    if (tz.startsWith('europe/')) return 'europe';
    if (tz.startsWith('asia/')) return 'asia';
    if (tz.startsWith('africa/')) return 'africa';
    if (tz.startsWith('australia/') || tz.startsWith('pacific/')) return 'oceania';
    if (tz.startsWith('america/')) {
      if (/(argentina|buenos_aires|sao_paulo|fortaleza|recife|bahia|belem|manaus|cuiaba|campo_grande|santiago|bogota|lima|caracas|montevideo|asuncion|guayaquil|la_paz|paramaribo|guyana|cayenne)/.test(tz)) return 'south-america';
      return 'north-america';
    }
  } catch {}

  return 'global';
}

interface LogCatchViewProps {
  onCatchAdded: (catchEntry: LogEntry) => void;
  onCancel: () => void;
  userId: string;
  prefilledData?: {
    species?: string;
    bait?: string;
    lureOrBait?: string;
    lureOrBaitType?: 'Lure' | 'Bait';
    technique?: string;
    waterCondition?: string;
    notes?: string;
    timeHint?: string;
    source?: 'fishiq' | 'direct';
    quickLog?: boolean;
    returnTo?: 'big_blue';
  } | null;
  weatherData: WeatherData | null;
  coords: { lat: number; lon: number } | null;
  tempUnit: 'Celsius' | 'Fahrenheit';
  userProfile: UserProfile;
  navigateTo: (view: View, payload?: any) => void;
  addToast: (message: string, type?: ToastType) => void;
  catchLog?: LogEntry[];
  catchDiaryEntries?: LogEntry[];
  initialTab?: string;
  initialQuery?: string;
  openInsight?: boolean;
  catchId?: string;
  onDisablePostCatchInsight?: () => void | Promise<void>;
  onCatchLogged?: (coords: { lat: number; lon: number } | null, catchEntry?: LogEntry | null) => void;
  onSaveSuccess?: () => void;
  onPostLogInsight?: (payload: PostLogInsightPayload) => void;
  onReturnAfterSave?: (payload?: { coords: { lat: number; lon: number } | null; catchEntry: LogEntry }) => void;
  tournamentContext?: TournamentLiveContext | null;
  editEntry?: LogEntry | null;
}

type EquippedState = {
  rodId?: string | null;
  reelId?: string | null;
  lureOrBaitId?: string | null;
};

type SubmitPhase = 'idle' | 'saving' | 'success';

type PendingInsightRoute = {
  createdCommunityPostId: string | null;
  coords: { lat: number; lon: number } | null;
};

export type PostLogInsightPayload = {
  catchEntry: LogEntry;
  nearbyCatchCount?: number;
  weatherText?: string | null;
  onDone: () => void;
};

const CUSTOM_VALUE = '__custom__';
const MAX_IMAGES = 8;
const SUCCESS_HOLD_MS = 920;

const LOG_CATCH_DRAFT_VERSION = 1;
const LOG_CATCH_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const logCatchDraftKey = (uid: string) =>
  `roddo.logCatch.draft.v1.${String(uid || 'guest').trim() || 'guest'}`;

const readLogCatchDraft = (uid: string): any | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(logCatchDraftKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number(parsed?.version || 0) !== LOG_CATCH_DRAFT_VERSION) return null;
    const updatedAt = Number(parsed?.updatedAt || 0);
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > LOG_CATCH_DRAFT_MAX_AGE_MS) {
      window.localStorage.removeItem(logCatchDraftKey(uid));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeLogCatchDraft = (uid: string, draft: Record<string, any>) => {
  try {
    if (typeof window === 'undefined') return;

    const key = logCatchDraftKey(uid);
    const nextComparable = { version: LOG_CATCH_DRAFT_VERSION, ...draft };

    // Avoid rewriting an identical draft from overlapping autosave/pagehide paths.
    // updatedAt is intentionally excluded from the comparison so unchanged form state
    // remains a no-op instead of generating another localStorage write.
    try {
      const existingRaw = window.localStorage.getItem(key);
      if (existingRaw) {
        const existing = JSON.parse(existingRaw);
        const { updatedAt: _existingUpdatedAt, ...existingComparable } = existing || {};
        if (JSON.stringify(existingComparable) === JSON.stringify(nextComparable)) return;
      }
    } catch {}

    window.localStorage.setItem(
      key,
      JSON.stringify({ ...nextComparable, updatedAt: Date.now() }),
    );
  } catch {}
};

const clearLogCatchDraft = (uid: string) => {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(logCatchDraftKey(uid));
  } catch {}
};


const padLocalDatePart = (value: number) => String(value).padStart(2, '0');

const getLocalDateInputValue = (value: Date = new Date()) => {
  const year = value.getFullYear();
  const month = padLocalDatePart(value.getMonth() + 1);
  const day = padLocalDatePart(value.getDate());
  return `${year}-${month}-${day}`;
};

const getLocalTimeInputValue = (value: Date = new Date()) => {
  const hours = padLocalDatePart(value.getHours());
  const minutes = padLocalDatePart(value.getMinutes());
  return `${hours}:${minutes}`;
};

const buildLocalDateTimeMs = (dateValue: string, timeValue: string) => {
  const [year, month, day] = String(dateValue || '').split('-').map(Number);
  const [hours, minutes] = String(timeValue || '').split(':').map(Number);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return NaN;
  }

  return new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
};


const buildGeneralizedRegionKey = (coords?: { lat?: number; lon?: number } | null, location?: string | null) => {
  const lat = Number(coords?.lat);
  const lon = Number(coords?.lon);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    // Roughly city/region-scale. Never use exact GPS for pilot exports.
    return `geo2:${lat.toFixed(2)},${lon.toFixed(2)}`;
  }
  const label = String(location || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return label ? `place:${label}` : null;
};

const coordsFallback = (lat: number, lon: number) => `${lat.toFixed(4)}, ${lon.toFixed(4)}`;

const COORDINATE_LABEL_RE = /^\s*[-+]?\d{1,3}(?:\.\d+)?\s*[,/]\s*[-+]?\d{1,3}(?:\.\d+)?\s*$/;
const ADDRESSISH_PART_RE = /^\d{1,7}\s+|\b(?:street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|drive|dr\.?|lane|ln\.?|court|ct\.?|highway|hwy\.?|parkway|pkwy\.?|route)\b/i;
const COUNTRY_PART_RE = /^(?:united states(?: of america)?|u\.?s\.?a?\.?|canada|mexico)$/i;

const looksLikeCoordinateLabel = (value: unknown): boolean =>
  COORDINATE_LABEL_RE.test(String(value ?? '').trim());

/**
 * Public community posts must never inherit the precise label used by the
 * private catch diary. Map/GPS-selected catches are reduced to a broad
 * city/region-style label when one can be inferred safely. If the reverse
 * geocoder failed or only a precise/single-point label is available, the Bay
 * gets an explicit privacy label instead of coordinates.
 */
const buildPublicCommunityLocationLabel = (
  value: unknown,
  hasPreciseCoords: boolean,
): string => {
  const raw = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!raw || looksLikeCoordinateLabel(raw)) return 'Location private';

  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !COUNTRY_PART_RE.test(part))
    .filter((part) => !/^\d{5}(?:-\d{4})?$/.test(part))
    .filter((part) => !/\bcounty\b/i.test(part))
    .filter((part) => !ADDRESSISH_PART_RE.test(part));

  if (parts.length >= 2) {
    const city = parts[0];
    const region = parts[parts.length - 1];
    const broad = `${city}, ${region}`.slice(0, 96);
    return looksLikeCoordinateLabel(broad) ? 'Location private' : broad;
  }

  // A single label selected from an exact map point may be a named dock, ramp,
  // cove, lake access, etc. Do not guess that it is safe to publish.
  if (hasPreciseCoords) return 'Location private';

  return parts[0]?.slice(0, 96) || 'Location private';
};

const resolvePlaceLabel = async (lat: number, lon: number): Promise<string> => {
  try {
    const label = await reverseGeocodeServer(lat, lon);
    return typeof label === 'string' && label.trim() ? label : coordsFallback(lat, lon);
  } catch (error) {
    console.error('Reverse geocoding failed (server)', error);
    return coordsFallback(lat, lon);
  }
};

const createLocationPickerPinIcon = (isDarkMode: boolean) =>
  L.divIcon({
    className: 'roddo-location-picker-pin bg-transparent',
    html: `<div style="position:relative;width:38px;height:44px;filter:drop-shadow(0 7px 10px rgba(2,8,23,.38));"><div style="position:absolute;left:6px;top:2px;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${isDarkMode ? '#67D8E8' : '#315D9C'};border:3px solid #fff;box-sizing:border-box;"><span style="position:absolute;left:50%;top:50%;width:7px;height:7px;border-radius:999px;background:#fff;transform:translate(-50%,-50%) rotate(45deg);"></span></div></div>`,
    iconSize: [38, 44],
    iconAnchor: [19, 38],
    popupAnchor: [0, -36],
  });

const cleanObject = <T extends Record<string, any>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as T;

function getCatchImages(entry: LogEntry): string[] {
  const e: any = entry as any;
  if (Array.isArray(e.images) && e.images.length) return e.images.filter(Boolean);
  if (Array.isArray(e.imageUrls) && e.imageUrls.length) return e.imageUrls.filter(Boolean);
  const legacy = e.imageUrl ?? e.image ?? null;
  if (typeof legacy === 'string' && legacy) return [legacy];
  return [];
}

const uploadCatchImage = async (args: {
  uid: string;
  catchId: string;
  file: File;
  index: number;
  isPublic: boolean;
}): Promise<string> => {
  const basePath = args.isPublic ? 'community' : 'catchDiaryPrivate';
  const path = `${basePath}/${args.uid}/${args.catchId}/${args.index}.jpg`;

  return await uploadImage(args.file, path, {
    maxSizeMB: 10,
    maxDim: 1600,
    contentType: 'image/jpeg',
    alreadyCompressed: true,
  });
};

const RAW_IMAGE_LIMIT_BYTES = 48 * 1024 * 1024;
const LOG_IMAGE_TARGET_BYTES = 5 * 1024 * 1024;

const makeLogImageFile = async (file: File): Promise<File> => {
  const sourceName = file.name || `roddo-catch-${Date.now()}.jpg`;
  const safeBaseName = sourceName.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'roddo-catch';

  if (file.size > RAW_IMAGE_LIMIT_BYTES) {
    throw new Error('IMAGE_TOO_LARGE_RAW');
  }

  const attempts = [
    { maxDim: 1600, quality: 0.82 },
    { maxDim: 1400, quality: 0.78 },
    { maxDim: 1200, quality: 0.74 },
    { maxDim: 1024, quality: 0.70 },
    { maxDim: 900, quality: 0.66 },
  ];

  let lastBlob: Blob | null = null;

  for (const attempt of attempts) {
    const blob = await compressImageFile(file, {
      maxDim: attempt.maxDim,
      quality: attempt.quality,
      mimeType: 'image/jpeg',
    });

    lastBlob = blob;
    if (blob.size <= LOG_IMAGE_TARGET_BYTES) {
      return new File([blob], `${safeBaseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
  }

  if (lastBlob) {
    return new File([lastBlob], `${safeBaseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  }

  throw new Error('IMAGE_COMPRESS_FAILED');
};

function getTimeBucketLocal(ms: number): 'morning' | 'afternoon' | 'evening' | 'night' {
  const h = new Date(ms).getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
}

function getMonthMMLocal(ms: number): string {
  return String(new Date(ms).getMonth() + 1).padStart(2, '0');
}

function useRoddoScrollGlow<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined') return;

    const findScrollParent = (): HTMLElement | Window => {
      let el: HTMLElement | null = node.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const canScroll = /(auto|scroll|overlay)/.test(style.overflowY);
        if (canScroll && el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return window;
    };

    const scroller = findScrollParent();
    let raf = 0;

    const readScroll = () => {
      if (scroller === window) {
        const doc = document.scrollingElement || document.documentElement;
        return { top: window.scrollY || doc.scrollTop || 0, max: Math.max(1, doc.scrollHeight - window.innerHeight) };
      }
      const el = scroller as HTMLElement;
      return { top: el.scrollTop, max: Math.max(1, el.scrollHeight - el.clientHeight) };
    };

    const update = () => {
      raf = 0;
      const { top, max } = readScroll();
      const progress = Math.max(0, Math.min(1, top / max));
      const eased = 1 - Math.pow(1 - progress, 2);
      const x = 48 + Math.sin(progress * Math.PI) * 8;
      const y = 12 + eased * 58;
      const opacity = 0.72 + progress * 0.18;

      node.style.setProperty('--roddo-scroll-glow-x', `${x.toFixed(1)}%`);
      node.style.setProperty('--roddo-scroll-glow-y', `${y.toFixed(1)}%`);
      node.style.setProperty('--roddo-scroll-glow-opacity', opacity.toFixed(2));
    };

    const requestUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    const target = scroller === window ? window : (scroller as HTMLElement);
    target.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      target.removeEventListener('scroll', requestUpdate as EventListener);
      window.removeEventListener('resize', requestUpdate as EventListener);
    };
  }, []);

  return ref;
}

function getRoddoScrollGlowStyle(isDarkMode: boolean): React.CSSProperties {
  // Night mode stays quiet and structural. Light mode keeps the soft stationery glow.
  if (isDarkMode) {
    return {
      backgroundImage: 'none',
      backgroundRepeat: 'no-repeat',
    };
  }

  return {
    backgroundImage:
      'radial-gradient(720px 420px at var(--roddo-scroll-glow-x, 50%) var(--roddo-scroll-glow-y, 18%), rgba(255,214,150,0.18), transparent 70%), radial-gradient(520px 360px at calc(var(--roddo-scroll-glow-x, 50%) + 18%) calc(var(--roddo-scroll-glow-y, 18%) + 16%), rgba(103,232,249,0.075), transparent 72%)',
    backgroundRepeat: 'no-repeat',
  };
}


function GalleryLightbox({
  images,
  startIndex,
  title,
  onClose,
}: {
  images: string[];
  startIndex: number;
  title?: string;
  onClose: () => void;
}) {
  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const [idx, setIdx] = useState(() => {
    const n = Number(startIndex);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(safeImages.length - 1, n));
  });

  useEffect(() => {
    setIdx((prev) => Math.max(0, Math.min(safeImages.length - 1, prev)));
  }, [safeImages.length]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (safeImages.length > 1 && e.key === 'ArrowLeft') setIdx((v) => (v - 1 + safeImages.length) % safeImages.length);
      if (safeImages.length > 1 && e.key === 'ArrowRight') setIdx((v) => (v + 1) % safeImages.length);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, safeImages.length]);

  const src = safeImages[idx];

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] grid place-items-center bg-black/85 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-[max(12px,env(safe-area-inset-top))] backdrop-blur-sm"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-10 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/10 text-white shadow-[0_6px_0_rgba(0,0,0,0.35)] hover:bg-white/15"
          aria-label="Close"
          title="Close"
        >
          ✕
        </button>

        <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/30 shadow-2xl">
          {src ? (
            <img
              src={src}
              alt={title || 'Expanded'}
              className="w-full cursor-zoom-out object-contain" style={{ maxHeight: 'min(80vh, calc(var(--roddo-visual-vh, 100dvh) - max(48px, env(safe-area-inset-top)) - max(48px, env(safe-area-inset-bottom))))' }}
              onClick={onClose}
            />
          ) : null}
        </div>

        {safeImages.length > 1 && (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-white/70">
                {idx + 1} / {safeImages.length}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIdx((v) => (v - 1 + safeImages.length) % safeImages.length)}
                  className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  onClick={() => setIdx((v) => (v + 1) % safeImages.length)}
                  className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                >
                  Next →
                </button>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
              {safeImages.map((u, i) => (
                <button
                  key={`${u}-${i}`}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-2xl border transition ${
                    i === idx ? 'border-[#ffcd64] ring-2 ring-[#ffcd64]/40' : 'border-white/15 hover:border-white/30'
                  }`}
                  title={`View ${i + 1}`}
                >
                  <img src={u} alt={`thumb-${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

const LogCatchView: React.FC<LogCatchViewProps> = ({
  onCatchAdded,
  onCancel,
  userId,
  prefilledData,
  weatherData,
  coords,
  tempUnit,
  userProfile,
  navigateTo,
  addToast,
  catchLog,
  catchDiaryEntries,
  initialTab,
  initialQuery,
  openInsight,
  catchId,
  onDisablePostCatchInsight,
  onCatchLogged,
  onSaveSuccess,
  onPostLogInsight,
  onReturnAfterSave,
  tournamentContext,
  editEntry,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    trackRoddoEvent('log_catch_opened', { source: prefilledData?.species ? 'fishiq' : tournamentContext?.tournamentId ? 'tournament' : 'direct', account_type: (userProfile as any)?.accountType === 'guest' ? 'guest' : 'registered', tournament_mode: Boolean(tournamentContext?.tournamentId), edit_mode: Boolean(editEntry && String((editEntry as any)?.id || '').trim()) });
  }, []);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveRoddoDarkMode());
  const scrollGlowRef = useRoddoScrollGlow<HTMLDivElement>();
  const scrollGlowStyle = useMemo(() => getRoddoScrollGlowStyle(isDarkMode), [isDarkMode]);
  useEffect(() => {
    let lastTheme = resolveRoddoDarkMode();

    const syncTheme = () => {
      const nextTheme = resolveRoddoDarkMode();
      if (nextTheme !== lastTheme) {
        lastTheme = nextTheme;
        setIsDarkMode(nextTheme);
      }
    };

    setIsDarkMode(lastTheme);

    const media = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
    const observer = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => {
          window.requestAnimationFrame(syncTheme);
        })
      : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
      if (document.body) {
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-theme', 'style'],
        });
      }
    }

    const intervalId = window.setInterval(syncTheme, 220);

    window.addEventListener('rodo-theme-change', syncTheme as EventListener);
    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);
    window.addEventListener('focus', syncTheme as EventListener);
    window.addEventListener('pageshow', syncTheme as EventListener);
    window.addEventListener('visibilitychange', syncTheme as EventListener);
    media?.addEventListener?.('change', syncTheme);

    return () => {
      observer?.disconnect();
      window.clearInterval(intervalId);
      window.removeEventListener('rodo-theme-change', syncTheme as EventListener);
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
      window.removeEventListener('focus', syncTheme as EventListener);
      window.removeEventListener('pageshow', syncTheme as EventListener);
      window.removeEventListener('visibilitychange', syncTheme as EventListener);
      media?.removeEventListener?.('change', syncTheme);
    };
  }, []);

  const theme = getRoddoTheme(isDarkMode);
  const ui = getRoddoUiTokens(isDarkMode);

  const isEditMode = Boolean(editEntry && String((editEntry as any)?.id || '').trim());
  const editRaw: any = (editEntry as any) || null;
  const existingEditImages = useMemo(() => isEditMode ? getCatchImages(editRaw as LogEntry) : [], [isEditMode, editRaw]);

  const isTournamentMode = Boolean(tournamentContext?.tournamentId);
  const isFishIQQuickLog = Boolean(
    !isTournamentMode &&
    !isEditMode &&
    prefilledData?.source === 'fishiq' &&
    prefilledData?.quickLog === true,
  );
  const tournamentCapturePolicy = tournamentContext?.submissionCapturePolicy || 'live_only';
  const tournamentAllowsUploads = tournamentCapturePolicy === 'live_or_upload';
  const tournamentMeasurementUnit = tournamentContext?.measurementUnit || 'in';
  const tournamentMeasurementRequired = isTournamentMode && !['most_catches', 'species_count', 'checklist'].includes(
    String(tournamentContext?.scoringMode || ''),
  );

  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lon: number } | null>(coords);
  const hasAuthoritativeRouteCoords = Boolean(
    coords &&
      Number.isFinite(Number(coords.lat)) &&
      Number.isFinite(Number(coords.lon)) &&
      (prefilledData as any)?.coordinateLocked === true,
  );
  const [species, setSpecies] = useState('');
  const [weight, setWeight] = useState('');
  const [date, setDate] = useState(() => getLocalDateInputValue());
  const [time, setTime] = useState(() => getLocalTimeInputValue());
  const [location, setLocation] = useState('');
  const [weatherOverride, setWeatherOverride] = useState('');
  const [waterCondition, setWaterCondition] = useState('');
  const [observationType, setObservationType] = useState<FishIQObservationType>('catch');
  const [activityClues, setActivityClues] = useState<FishIQActivityClue[]>([]);
  const [missedBiteCount, setMissedBiteCount] = useState('');
  const [followCount, setFollowCount] = useState('');
  const [waterClarity, setWaterClarity] = useState<'clear' | 'stained' | 'muddy' | 'unknown'>('unknown');
  const [waterLevel, setWaterLevel] = useState<'low' | 'normal' | 'high' | 'unknown'>('unknown');
  const [catchDisposition, setCatchDisposition] = useState<'released' | 'harvested' | 'unknown'>('unknown');
  const [effortMinutes, setEffortMinutes] = useState('');
  const [rod, setRod] = useState('');
  const [reel, setReel] = useState('');
  const [lureOrBait, setLureOrBait] = useState('');
  const [rodId, setRodId] = useState<string>('');
  const [reelId, setReelId] = useState<string>('');
  const [lureOrBaitId, setLureOrBaitId] = useState<string>('');
  const [rodMode, setRodMode] = useState<'select' | 'custom'>('select');
  const [reelMode, setReelMode] = useState<'select' | 'custom'>('select');
  const [lureMode, setLureMode] = useState<'select' | 'custom'>('select');
  const [lureOrBaitType, setLureOrBaitType] = useState<'Lure' | 'Bait'>('Lure');
  const [technique, setTechnique] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');
  const [isSubmitPressed, setIsSubmitPressed] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isScanningFish, setIsScanningFish] = useState(false);
  const [scanResult, setScanResult] = useState<FishScanResult | null>(null);
  const [scanImageUrl, setScanImageUrl] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isPhotoChooserOpen, setIsPhotoChooserOpen] = useState(false);
  const [photoConfirm, setPhotoConfirm] = useState<null | { mode: 'single'; index: number } | { mode: 'all' }>(null);
  const [clearCatchConfirmOpen, setClearCatchConfirmOpen] = useState(false);
  const isMountedRef = useRef(true);
  const submitLockRef = useRef(false);
  const submitAttemptCatchIdRef = useRef<string | null>(null);
  const [gallery, setGallery] = useState<{ open: boolean; images: string[]; startIndex: number; title?: string }>({
    open: false,
    images: [],
    startIndex: 0,
    title: undefined,
  });
  const [insightCatch, setInsightCatch] = useState<LogEntry | null>(null);
  const [lastInsightCatch, setLastInsightCatch] = useState<LogEntry | null>(null);
  const [isInsightOpen, setIsInsightOpen] = useState(false);
  const [pendingInsightRoute, setPendingInsightRoute] = useState<PendingInsightRoute | null>(null);
  const handledRouteInsightRef = useRef<string | null>(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [shareToCommunity, setShareToCommunity] = useState(false);
  const [showFieldNotes, setShowFieldNotes] = useState(false);
  const [showQuickExtras, setShowQuickExtras] = useState(false);
  const [tournamentMeasurement, setTournamentMeasurement] = useState('');
  const [tournamentGpsVerified, setTournamentGpsVerified] = useState(false);
  const [tournamentCaptureSource, setTournamentCaptureSource] = useState<TournamentCaptureSource>('live_camera');
  const [tournamentRetryCatchId, setTournamentRetryCatchId] = useState<string | null>(null);
  const editHydratedIdRef = useRef<string | null>(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const draftRecoveredRef = useRef(false);
  const catchPersistedRef = useRef(false);

  useEffect(() => {
    if (!isEditMode || !editRaw) {
      editHydratedIdRef.current = null;
      return;
    }

    const editId = String(editRaw.id || editRaw.catchId || '').trim();
    if (!editId || editHydratedIdRef.current === editId) return;
    editHydratedIdRef.current = editId;

    const editTimestamp = Number(editRaw.timestamp || editRaw.catchTimestamp || 0) || Date.now();
    const editDate = new Date(editTimestamp);
    const dateValue = String(editRaw.date || editRaw.localDate || '').trim() || getLocalDateInputValue(editDate);
    const timeValue = String(editRaw.time || editRaw.localTime || '').trim() || getLocalTimeInputValue(editDate);

    setSpecies(String(editRaw.species || editRaw.speciesName || ''));
    setWeight(String(editRaw.weight || ''));
    setDate(dateValue);
    setTime(timeValue);
    setLocation(String(editRaw.location || editRaw.locationName || editRaw.waterName || ''));
    setWeatherOverride(String(editRaw.weather || editRaw.weatherSnapshot?.conditionText || ''));
    setWaterCondition(String(editRaw.waterCondition || editRaw.conditions || editRaw.waterSnapshot?.waterCondition || ''));

    const lat = Number(editRaw.coords?.lat ?? editRaw.lat ?? editRaw.latitude);
    const lon = Number(editRaw.coords?.lon ?? editRaw.coords?.lng ?? editRaw.lon ?? editRaw.lng ?? editRaw.longitude);
    setSelectedCoords(Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null);

    setRod(String(editRaw.rod || editRaw.rodName || editRaw.equipment?.rod || ''));
    setReel(String(editRaw.reel || editRaw.reelName || editRaw.equipment?.reel || ''));
    setLureOrBait(String(editRaw.lureOrBait || editRaw.lureOrBaitName || editRaw.lure || editRaw.bait || editRaw.equipment?.lureOrBait || ''));
    setTechnique(String(editRaw.technique || editRaw.equipment?.technique || ''));

    const nextRodId = String(editRaw.rodId || editRaw.equipment?.rodId || '');
    const nextReelId = String(editRaw.reelId || editRaw.equipment?.reelId || '');
    const nextLureId = String(editRaw.lureOrBaitId || editRaw.equipment?.lureOrBaitId || '');
    setRodId(nextRodId);
    setReelId(nextReelId);
    setLureOrBaitId(nextLureId);
    setRodMode(nextRodId ? 'select' : 'custom');
    setReelMode(nextReelId ? 'select' : 'custom');
    setLureMode(nextLureId ? 'select' : 'custom');
    setLureOrBaitType(editRaw.lureOrBaitType === 'Bait' ? 'Bait' : 'Lure');
    setUserEditedRod(true);
    setUserEditedReel(true);
    setUserEditedLureBait(true);

    setObservationType((editRaw.observationType || 'catch') as FishIQObservationType);
    setActivityClues(Array.isArray(editRaw.activityClues) ? editRaw.activityClues : []);
    setMissedBiteCount(editRaw.missedBiteCount == null ? '' : String(editRaw.missedBiteCount));
    setFollowCount(editRaw.followCount == null ? '' : String(editRaw.followCount));
    setWaterClarity((editRaw.waterClarity || editRaw.waterSnapshot?.waterClarity || 'unknown') as any);
    setWaterLevel((editRaw.waterLevel || editRaw.waterSnapshot?.waterLevel || 'unknown') as any);
    setCatchDisposition((editRaw.catchDisposition || 'unknown') as any);
    setEffortMinutes(editRaw.effortMinutes == null ? '' : String(editRaw.effortMinutes));
    setShareToCommunity(false);
  }, [isEditMode, editRaw]);
  useEffect(() => {
    if (isTournamentMode) setShareToCommunity(false);
  }, [isTournamentMode]);

  const contributeToFishIQ = useMemo(() => {
    const legalConsent = (userProfile as any)?.legal?.consents?.fishiqContribution?.enabled === true;
    return (userProfile as any)?.fishiqContributionEnabled === true || legalConsent;
  }, [userProfile]);
  const [gearItems, setGearItems] = useState<any[]>(((userProfile as any).tackleBox as any[]) ?? []);
  const [localEquipped, setLocalEquipped] = useState<EquippedState>(() => {
    const eg = (userProfile as any)?.equippedGear ?? {};
    return {
      rodId: eg.rodId ?? null,
      reelId: eg.reelId ?? null,
      lureOrBaitId: eg.lureOrBaitId ?? eg.lureId ?? null,
    };
  });
  const [userEditedRod, setUserEditedRod] = useState(false);
  const [userEditedReel, setUserEditedReel] = useState(false);
  const [userEditedLureBait, setUserEditedLureBait] = useState(false);

  const toggleActivityClue = (value: FishIQActivityClue) => {
    setActivityClues((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  };
  const lastAutoRodIdRef = useRef<string>('');
  const lastAutoReelIdRef = useRef<string>('');
  const lastAutoLureOrBaitIdRef = useRef<string>('');

  const allCatchEntriesForInsight = useMemo(() => {
    const merged = [...((catchDiaryEntries as any[]) || []), ...((catchLog as any[]) || [])];
    const seen = new Set<string>();
    return merged.filter((entry: any) => {
      const id = String(entry?.id || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    }) as LogEntry[];
  }, [catchDiaryEntries, catchLog]);

  const persistInsightState = async (entry: LogEntry | null | undefined, patch: Record<string, any>) => {
    const persistedCatchId = String((entry as any)?.id || (entry as any)?.catchId || (entry as any)?.sourceId || '').trim();
    if (!userId || !persistedCatchId) return;

    try {
      await setDoc(
        doc(db, 'users', userId, 'catchDiary', persistedCatchId),
        cleanObject({
          insightAvailable: true,
          ...patch,
        }),
        { merge: true },
      );
    } catch (error) {
      console.warn('[LogCatchView] insight persistence failed (non-blocking)', error);
    }
  };

  const markInsightOpened = (entry: LogEntry | null | undefined) => {
    if (!entry) return;
    void persistInsightState(entry, {
      insightSeen: true,
      insightDismissed: false,
      insightLastOpenedAt: Date.now(),
    });
  };

  const markInsightClosed = (entry: LogEntry | null | undefined) => {
    if (!entry) return;
    void persistInsightState(entry, {
      insightSeen: true,
      insightDismissed: true,
      insightDismissedAt: Date.now(),
    });
  };

  const shouldAutoOpenInsightForCatch = (entry: LogEntry | null | undefined) => {
    if (!entry) return false;

    const userAllowsPostCatchInsight = (userProfile as any)?.notificationSettings?.showPostCatchInsight !== false;
    const insightAvailable = (entry as any)?.insightAvailable !== false;
    const insightSeen = (entry as any)?.insightSeen === true;
    const insightDismissed = (entry as any)?.insightDismissed === true;

    return userAllowsPostCatchInsight && insightAvailable && !insightSeen && !insightDismissed;
  };

  useEffect(() => {
    const requestedCatchId = String(catchId || '').trim();

    if (!openInsight || !requestedCatchId) {
      handledRouteInsightRef.current = null;
      return;
    }

    if (handledRouteInsightRef.current === requestedCatchId) return;

    const match = allCatchEntriesForInsight.find((entry: any) => {
      const ids = [entry?.id, entry?.catchId, entry?.sourceId]
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      return ids.includes(requestedCatchId);
    });

    if (!match) {
      console.warn('[LogCatchView] requested insight catch not found', requestedCatchId);
      return;
    }

    handledRouteInsightRef.current = requestedCatchId;
    setPendingInsightRoute(null);
    setInsightCatch(match);
    setLastInsightCatch(match);
    markInsightOpened(match);
    setIsInsightOpen(true);
  }, [openInsight, catchId, allCatchEntriesForInsight]);

  useEffect(() => {
    if (!userProfile?.uid) return;
    const unsubscribe = subscribeToGear(userProfile.uid, (items) => {
      setGearItems(items as any[]);
    });
    return () => unsubscribe();
  }, [userProfile?.uid]);

  const rods = useMemo(() => (gearItems ?? []).filter((i: any) => i.category === 'rod'), [gearItems]);
  const reels = useMemo(() => (gearItems ?? []).filter((i: any) => i.category === 'reel'), [gearItems]);
  const luresBaits = useMemo(
    () => (gearItems ?? []).filter((i: any) => i.category === 'lure' || i.category === 'bait'),
    [gearItems],
  );

  useEffect(() => {
    if (!userProfile?.uid) {
      setLocalEquipped({ rodId: null, reelId: null, lureOrBaitId: null });
      return;
    }

    const unsubscribe = subscribeToEquippedGear(
      userProfile.uid,
      (equipped) => {
        setLocalEquipped({
          rodId: equipped.rodId ?? null,
          reelId: equipped.reelId ?? null,
          lureOrBaitId: equipped.lureOrBaitId ?? equipped.lureId ?? null,
        });
      },
      (err) => console.error('[LogCatchView] subscribeToEquippedGear error:', err),
    );

    return () => unsubscribe();
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!coords) return;

    if (hasAuthoritativeRouteCoords) {
      const next = { lat: Number(coords.lat), lon: Number(coords.lon) };
      setSelectedCoords((current) =>
        current && current.lat === next.lat && current.lon === next.lon ? current : next,
      );
      return;
    }

    if (!selectedCoords) setSelectedCoords(coords);
  }, [coords, hasAuthoritativeRouteCoords, selectedCoords]);

  const prefilledLureOrBait = String(prefilledData?.lureOrBait || prefilledData?.bait || '').trim();

  useEffect(() => {
    if (!isEditMode && prefilledData?.species) setSpecies(prefilledData.species);
  }, [prefilledData?.species, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    if (prefilledLureOrBait) {
      setLureMode('custom');
      setLureOrBaitId('');
      setLureOrBait(prefilledLureOrBait);
      setLureOrBaitType(prefilledData?.lureOrBaitType || 'Lure');
      setUserEditedLureBait(true);
    }

    if (prefilledData?.technique?.trim()) {
      setTechnique(prefilledData.technique.trim());
    }

    const planNote = String(prefilledData?.waterCondition || prefilledData?.notes || '').trim();
    if (planNote) {
      setWaterCondition((prev) => (prev.trim() ? prev : planNote));
    }
  }, [isEditMode, prefilledLureOrBait, prefilledData?.lureOrBaitType, prefilledData?.technique, prefilledData?.waterCondition, prefilledData?.notes]);

  // Release hardening: keep unfinished non-tournament catch form data on-device.
  // Photos are intentionally NOT persisted in localStorage; a recovered draft tells
  // the angler to re-add them after a hard app kill/reload.
  useEffect(() => {
    if (draftHydrated) return;
    if (isEditMode || isTournamentMode) {
      setDraftHydrated(true);
      return;
    }

    const draft = readLogCatchDraft(userId);
    if (draft) {
      draftRecoveredRef.current = true;
      if (draft.species) setSpecies((prev) => prev.trim() ? prev : String(draft.species));
      if (draft.weight) setWeight((prev) => prev.trim() ? prev : String(draft.weight));
      if (draft.date) setDate(String(draft.date));
      if (draft.time) setTime(String(draft.time));
      if (draft.location && !hasAuthoritativeRouteCoords) {
        setLocation((prev) => prev.trim() ? prev : String(draft.location));
      }
      if (draft.weatherOverride) setWeatherOverride((prev) => prev.trim() ? prev : String(draft.weatherOverride));
      if (draft.waterCondition) setWaterCondition((prev) => prev.trim() ? prev : String(draft.waterCondition));
      if (draft.observationType) setObservationType(draft.observationType as FishIQObservationType);
      if (Array.isArray(draft.activityClues)) setActivityClues(draft.activityClues as FishIQActivityClue[]);
      if (draft.missedBiteCount != null) setMissedBiteCount(String(draft.missedBiteCount));
      if (draft.followCount != null) setFollowCount(String(draft.followCount));
      if (draft.waterClarity) setWaterClarity(draft.waterClarity);
      if (draft.waterLevel) setWaterLevel(draft.waterLevel);
      if (draft.catchDisposition) setCatchDisposition(draft.catchDisposition);
      if (draft.effortMinutes != null) setEffortMinutes(String(draft.effortMinutes));
      if (draft.rod) setRod(String(draft.rod));
      if (draft.reel) setReel(String(draft.reel));
      if (draft.lureOrBait) setLureOrBait(String(draft.lureOrBait));
      if (draft.rodId) setRodId(String(draft.rodId));
      if (draft.reelId) setReelId(String(draft.reelId));
      if (draft.lureOrBaitId) setLureOrBaitId(String(draft.lureOrBaitId));
      if (draft.rodMode) setRodMode(draft.rodMode);
      if (draft.reelMode) setReelMode(draft.reelMode);
      if (draft.lureMode) setLureMode(draft.lureMode);
      if (draft.lureOrBaitType) setLureOrBaitType(draft.lureOrBaitType);
      if (draft.technique) setTechnique((prev) => prev.trim() ? prev : String(draft.technique));
      if (typeof draft.shareToCommunity === 'boolean') setShareToCommunity(draft.shareToCommunity);
      const lat = Number(draft?.selectedCoords?.lat);
      const lon = Number(draft?.selectedCoords?.lon);
      if (
        !hasAuthoritativeRouteCoords &&
        Number.isFinite(lat) &&
        Number.isFinite(lon)
      ) {
        setSelectedCoords({ lat, lon });
      }

      // Draft recovery is intentionally silent. The restored form state is the feedback;
      // avoid stacking duplicate "Trail note" toasts during dev StrictMode/remounts.
    }

    setDraftHydrated(true);
  }, [draftHydrated, isEditMode, isTournamentMode, userId, addToast, hasAuthoritativeRouteCoords]);

  const currentDraftSnapshot = useMemo(() => ({
    species,
    weight,
    date,
    time,
    location,
    weatherOverride,
    waterCondition,
    observationType,
    activityClues,
    missedBiteCount,
    followCount,
    waterClarity,
    waterLevel,
    catchDisposition,
    effortMinutes,
    rod,
    reel,
    lureOrBait,
    rodId,
    reelId,
    lureOrBaitId,
    rodMode,
    reelMode,
    lureMode,
    lureOrBaitType,
    technique,
    shareToCommunity,
    selectedCoords,
    hadPhotos: imageFiles.length > 0,
  }), [
    species, weight, date, time, location, weatherOverride, waterCondition,
    observationType, activityClues, missedBiteCount, followCount, waterClarity,
    waterLevel, catchDisposition, effortMinutes, rod, reel, lureOrBait, rodId,
    reelId, lureOrBaitId, rodMode, reelMode, lureMode, lureOrBaitType, technique,
    shareToCommunity, selectedCoords, imageFiles.length,
  ]);

  const draftSnapshotRef = useRef(currentDraftSnapshot);
  useEffect(() => {
    draftSnapshotRef.current = currentDraftSnapshot;
  }, [currentDraftSnapshot]);

  useEffect(() => {
    if (!draftHydrated || isEditMode || isTournamentMode || isSubmitting || catchPersistedRef.current) return;

    const hasMeaningfulDraft = Boolean(
      species.trim() || weight.trim() || waterCondition.trim() || technique.trim() ||
      rod.trim() || reel.trim() || lureOrBait.trim() || activityClues.length ||
      missedBiteCount.trim() || followCount.trim() || effortMinutes.trim() || imageFiles.length
    );

    if (!hasMeaningfulDraft) {
      if (draftRecoveredRef.current) clearLogCatchDraft(userId);
      return;
    }

    const timer = window.setTimeout(() => {
      writeLogCatchDraft(userId, draftSnapshotRef.current);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [
    draftHydrated, isEditMode, isTournamentMode, isSubmitting, userId,
    species, weight, waterCondition, technique, rod, reel, lureOrBait,
    activityClues, missedBiteCount, followCount, effortMinutes, imageFiles.length,
    currentDraftSnapshot,
  ]);

  useEffect(() => {
    if (!draftHydrated || isEditMode || isTournamentMode) return;
    const persistNow = () => {
      if (catchPersistedRef.current) return;
      const snapshot = draftSnapshotRef.current;
      const meaningful = Boolean(
        String(snapshot.species || '').trim() || String(snapshot.weight || '').trim() ||
        String(snapshot.waterCondition || '').trim() || String(snapshot.technique || '').trim() ||
        String(snapshot.rod || '').trim() || String(snapshot.reel || '').trim() ||
        String(snapshot.lureOrBait || '').trim() || (snapshot.activityClues || []).length ||
        String(snapshot.missedBiteCount || '').trim() || String(snapshot.followCount || '').trim() ||
        String(snapshot.effortMinutes || '').trim() || snapshot.hadPhotos
      );
      if (meaningful) writeLogCatchDraft(userId, snapshot);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') persistNow();
    };
    window.addEventListener('pagehide', persistNow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', persistNow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [draftHydrated, isEditMode, isTournamentMode, userId]);

  useEffect(() => {
    if (!isEditMode && !weatherOverride.trim() && weatherData?.current) {
      const tempSymbol = tempUnit === 'Fahrenheit' ? '°F' : '°C';
      const derived = `${weatherData.current.condition}, ${weatherData.current.temp}${tempSymbol}, ${weatherData.current.wind}`;
      setWeatherOverride(derived);
    }
  }, [weatherData, tempUnit, weatherOverride, isEditMode]);

  useEffect(() => {
    if (!draftHydrated) return;
    if (!isEditMode && !location && coords) {
      let alive = true;
      setIsLocating(true);

      resolvePlaceLabel(coords.lat, coords.lon).then((placeName) => {
        if (!alive) return;
        setLocation(placeName);
        setIsLocating(false);
      });

      return () => {
        alive = false;
      };
    }
  }, [coords, location, isEditMode, draftHydrated]);

  useEffect(() => {
    const shouldAutoApplyRod =
      !isEditMode &&
      !!localEquipped.rodId &&
      rods.length > 0 &&
      (!userEditedRod || !rodId || rodId === lastAutoRodIdRef.current);

    if (shouldAutoApplyRod) {
      const item = rods.find((x: any) => x.id === localEquipped.rodId);
      if (item) {
        lastAutoRodIdRef.current = item.id;
        setRodMode('select');
        setRodId(item.id);
        setRod(item.name);
        setUserEditedRod(false);
      }
    }

    const shouldAutoApplyReel =
      !isEditMode &&
      !!localEquipped.reelId &&
      reels.length > 0 &&
      (!userEditedReel || !reelId || reelId === lastAutoReelIdRef.current);

    if (shouldAutoApplyReel) {
      const item = reels.find((x: any) => x.id === localEquipped.reelId);
      if (item) {
        lastAutoReelIdRef.current = item.id;
        setReelMode('select');
        setReelId(item.id);
        setReel(item.name);
        setUserEditedReel(false);
      }
    }

    const shouldAutoApplyLureOrBait =
      !isEditMode &&
      !!localEquipped.lureOrBaitId &&
      luresBaits.length > 0 &&
      !prefilledLureOrBait &&
      (!userEditedLureBait || !lureOrBaitId || lureOrBaitId === lastAutoLureOrBaitIdRef.current);

    if (shouldAutoApplyLureOrBait) {
      const item = luresBaits.find((x: any) => x.id === localEquipped.lureOrBaitId);
      if (item) {
        lastAutoLureOrBaitIdRef.current = item.id;
        setLureMode('select');
        setLureOrBaitId(item.id);
        setLureOrBait(item.name);
        setUserEditedLureBait(false);
        if (item.category === 'lure' || item.category === 'bait') {
          setLureOrBaitType(item.category === 'lure' ? 'Lure' : 'Bait');
        }
      }
    }
  }, [
    localEquipped,
    rods,
    reels,
    luresBaits,
    userEditedRod,
    userEditedReel,
    userEditedLureBait,
    prefilledLureOrBait,
    rodId,
    reelId,
    lureOrBaitId,
    isEditMode,
  ]);

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
    };
  }, [imagePreviewUrls]);

  useEffect(() => {
    // React StrictMode runs an effect setup -> cleanup -> setup cycle in dev.
    // Reset this ref during setup so delayed tournament-return callbacks are
    // still allowed to run after the StrictMode verification pass.
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openGallery = (images: string[], startIndex: number, title?: string) => {
    const safe = (images || []).filter(Boolean);
    if (!safe.length) return;
    setGallery({
      open: true,
      images: safe,
      startIndex: Math.max(0, Math.min(safe.length - 1, startIndex || 0)),
      title,
    });
  };

  const closeGallery = () => setGallery({ open: false, images: [], startIndex: 0, title: undefined });

  const onSelectRod = (val: string) => {
    setUserEditedRod(true);
    if (val === CUSTOM_VALUE) {
      setRodMode('custom');
      setRodId('');
      return;
    }
    setRodMode('select');
    setRodId(val);
    const item = rods.find((x: any) => x.id === val);
    setRod(item?.name ?? '');
  };

  const onSelectReel = (val: string) => {
    setUserEditedReel(true);
    if (val === CUSTOM_VALUE) {
      setReelMode('custom');
      setReelId('');
      return;
    }
    setReelMode('select');
    setReelId(val);
    const item = reels.find((x: any) => x.id === val);
    setReel(item?.name ?? '');
  };

  const onSelectLureBait = (val: string) => {
    setUserEditedLureBait(true);
    if (val === CUSTOM_VALUE) {
      setLureMode('custom');
      setLureOrBaitId('');
      return;
    }
    setLureMode('select');
    setLureOrBaitId(val);
    const item = luresBaits.find((x: any) => x.id === val);
    setLureOrBait(item?.name ?? '');
    if (item?.category === 'lure' || item?.category === 'bait') {
      setLureOrBaitType(item.category === 'lure' ? 'Lure' : 'Bait');
    }
  };

  const addPickedImages = async (
    files?: FileList | File[] | null,
    captureSource?: TournamentCaptureSource,
  ) => {
    if (!files) return;

    const arr: File[] = Array.isArray(files) ? files : Array.from(files);
    const openSlots = Math.max(0, MAX_IMAGES - imageFiles.length);
    const picked = arr.slice(0, openSlots || MAX_IMAGES);
    const accepted: File[] = [];

    if (!picked.length) return;

    for (const f of picked) {
      if (!f) continue;
      if (!f.type?.startsWith('image/')) {
        addToast(t('community.imageTypeError', 'Please choose an image file.'), 'error');
        continue;
      }

      try {
        const safeFile = await makeLogImageFile(f);
        accepted.push(safeFile);
      } catch (error) {
        console.error('[LogCatch] image compression failed', error);
        addToast(t('logCatch.photoTooLarge', 'That photo was too large for this device to process. Try a smaller photo or screenshot.'), 'error');
      }
    }

    if (imageInputRef.current) imageInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (!accepted.length) return;

    if (isTournamentMode && captureSource === 'uploaded_photo') {
      setTournamentCaptureSource('uploaded_photo');
    }

    const previewUrls = accepted.map((f) => URL.createObjectURL(f));
    setImageFiles((prev) => [...prev, ...accepted].slice(0, MAX_IMAGES));
    setImagePreviewUrls((prev) => [...prev, ...previewUrls].slice(0, MAX_IMAGES));

    // FishIQ quick-log already has an explicit species selection from the
    // prediction card. Do not run the photo identifier there; the photo is
    // evidence for the catch, not a competing species guess. Direct/area logs
    // still keep FishIQ Scan available as identification assistance.
    const fishIQPhotoScanEnabled =
      !isFishIQQuickLog && Boolean(FEATURE.FISHIQ || FEATURE.OCR);
    if (fishIQPhotoScanEnabled && !isScanningFish) {
      void handleFishIQScanFile(accepted[0], { attachToGallery: false, alreadyCompressed: true });
    }
  };


  const buildFishScanContext = () => {
    const expectedSpecies = String(
      isFishIQQuickLog ? prefilledData?.species || species : species,
    ).trim();

    return cleanObject({
      waterName: location?.trim() || undefined,
      location: location?.trim() || undefined,
      lat: selectedCoords?.lat,
      lon: selectedCoords?.lon,
      expectedSpecies: expectedSpecies || undefined,
      weather: weatherOverride?.trim() || undefined,
    });
  };

  const handleFishIQScanFile = async (
    file?: File | null,
    options?: { attachToGallery?: boolean; alreadyCompressed?: boolean },
  ) => {
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      addToast(t('community.imageTypeError', 'Please choose an image file.'), 'error');
      return;
    }

    let scanFile = file;

    try {
      scanFile = options?.alreadyCompressed ? file : await makeLogImageFile(file);
    } catch (error) {
      console.error('[FishIQ Scan] image prep failed', error);
      addToast('FishIQ could not prepare that photo. Try a smaller image.', 'warning');
      return;
    }

    if (options?.attachToGallery !== false) {
      const previewUrl = URL.createObjectURL(scanFile);
      setImageFiles((prev) => [scanFile, ...prev].slice(0, MAX_IMAGES));
      setImagePreviewUrls((prev) => [previewUrl, ...prev].slice(0, MAX_IMAGES));
    }

    setScanResult(null);
    setScanImageUrl(null);
    setIsScanningFish(true);

    try {
      const scanId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const uploadedUrl = await uploadScanImage(
        scanFile,
        userId || userProfile?.uid || 'unknown',
        scanId,
        true,
      );
      const result = await analyzeFishPhoto(uploadedUrl, buildFishScanContext());

      if (!isMountedRef.current) return;
      setScanImageUrl(uploadedUrl);
      setScanResult(result);
      addToast(t('logCatch.fishiqScanFinished', 'FishIQ photo scan finished. Review before using it.'), 'success');
    } catch (error) {
      console.error('[FishIQ Scan] failed', error);
      if (isMountedRef.current) {
        addToast('FishIQ could not read that photo yet. You can still log it manually.', 'warning');
      }
    } finally {
      if (isMountedRef.current) setIsScanningFish(false);
    }
  };

  const handleUseFishIQScanData = () => {
    if (!scanResult) return;

    const candidateSpecies = String(scanResult.species || '').trim();
    const candidateKey = candidateSpecies.toLowerCase();
    const currentSpecies = String(species || '').trim();
    const currentKey = currentSpecies.toLowerCase();
    const expectedSpecies = String(prefilledData?.species || '').trim();
    const expectedKey = expectedSpecies.toLowerCase();
    const rawConfidence = Number(scanResult.confidence);
    const speciesConfidence = Number.isFinite(rawConfidence)
      ? Math.max(0, Math.min(1, rawConfidence > 1 ? rawConfidence / 100 : rawConfidence))
      : 0;
    const candidateIsUsable = Boolean(candidateSpecies) && candidateKey !== 'unknown fish';
    const candidateDisagreesWithFishIQ = Boolean(
      isFishIQQuickLog &&
      expectedKey &&
      candidateIsUsable &&
      candidateKey !== expectedKey,
    );

    // Species identity is deliberately conservative. A weak photo scan must never
    // overwrite a species the angler explicitly chose through FishIQ, and a direct
    // log only receives a scan species when confidence is high enough to be useful.
    if (candidateIsUsable && !candidateDisagreesWithFishIQ && speciesConfidence >= 0.55) {
      setSpecies(candidateSpecies);
    } else if (candidateDisagreesWithFishIQ) {
      addToast(
        `Kept ${expectedSpecies}. FishIQ Scan suggested ${candidateSpecies} at ${Math.round(speciesConfidence * 100)}% confidence.`,
        'info',
      );
    } else if (candidateIsUsable && speciesConfidence < 0.55 && (!currentKey || candidateKey !== currentKey)) {
      addToast(
        `Kept ${currentSpecies || 'your species entry'}. FishIQ Scan was only ${Math.round(speciesConfidence * 100)}% confident in ${candidateSpecies}.`,
        'info',
      );
    }

    if (typeof scanResult.weightLbs === 'number' && Number.isFinite(scanResult.weightLbs)) {
      setWeight(`${scanResult.weightLbs.toFixed(scanResult.weightLbs >= 10 ? 1 : 2)} lbs`);
    }

    const lengthLine = typeof scanResult.lengthInches === 'number' && Number.isFinite(scanResult.lengthInches)
      ? `FishIQ Scan estimated length: ${scanResult.lengthInches.toFixed(1)} in.`
      : '';
    const noteLine = scanResult.notes?.trim() ? `FishIQ Scan note: ${scanResult.notes.trim()}` : '';
    const mergedNote = [lengthLine, noteLine].filter(Boolean).join('\n');
    if (mergedNote) {
      setWaterCondition((prev) => [prev?.trim(), mergedNote].filter(Boolean).join('\n\n'));
    }

    addToast(t('logCatch.fishiqScanAdded', 'FishIQ Scan supporting data added. Please double-check before saving.'), 'success');
  };

  const formatConfidence = (value?: number) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'low confidence';
    const normalized = value <= 1 ? value * 100 : value;
    return `${Math.max(0, Math.min(100, Math.round(normalized)))}% confidence`;
  };

  const removePickedImageAt = (idx: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviewUrls((prev) => {
      const target = prev[idx];
      if (target) {
        try {
          URL.revokeObjectURL(target);
        } catch {}
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearImages = () => {
    setImageFiles([]);
    if (isTournamentMode) setTournamentCaptureSource('live_camera');
    setImagePreviewUrls((prev) => {
      prev.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
      return [];
    });
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };


  const clearWorkingCatch = () => {
    // Keep the map/tournament context and selected location intact, but reset
    // the catch-specific working state so the angler can start this entry over.
    setSpecies('');
    setWeight('');
    setTournamentMeasurement('');
    setTechnique('');
    setWaterCondition('');
    setObservationType('catch');
    setActivityClues([]);
    setMissedBiteCount('');
    setFollowCount('');
    setWaterClarity('unknown');
    setWaterLevel('unknown');
    setCatchDisposition('unknown');
    setEffortMinutes('');

    setRod('');
    setReel('');
    setLureOrBait('');
    setRodId('');
    setReelId('');
    setLureOrBaitId('');
    setRodMode('select');
    setReelMode('select');
    setLureMode('select');
    setLureOrBaitType('Lure');
    setUserEditedRod(false);
    setUserEditedReel(false);
    setUserEditedLureBait(false);

    setScanResult(null);
    setScanImageUrl(null);
    setTournamentRetryCatchId(null);
    submitAttemptCatchIdRef.current = null;
    setSubmitPhase('idle');
    setIsSubmitPressed(false);
    setShareToCommunity(false);
    setShowFieldNotes(false);
    setShowQuickExtras(false);
    setIsPhotoChooserOpen(false);
    setPhotoConfirm(null);
    clearImages();

    if (!isTournamentMode) {
      setDate(getLocalDateInputValue());
      setTime(getLocalTimeInputValue());
      clearLogCatchDraft(userId);
      draftRecoveredRef.current = false;
      catchPersistedRef.current = false;
    }

    setClearCatchConfirmOpen(false);
    addToast('Catch form cleared. Location kept.', 'info');
  };

  const requestRemovePickedImageAt = (idx: number) => {
    setPhotoConfirm({ mode: 'single', index: idx });
  };

  const requestClearImages = () => {
    setPhotoConfirm({ mode: 'all' });
  };

  const confirmPhotoRemoval = () => {
    const target = photoConfirm;
    if (!target) return;
    if (target.mode === 'all') {
      clearImages();
    } else {
      removePickedImageAt(target.index);
    }
    setPhotoConfirm(null);
  };

  const getCameraFileExtension = (format?: string | null, mimeType?: string | null) => {
    const fmt = String(format || '').toLowerCase();
    const mime = String(mimeType || '').toLowerCase();
    if (fmt.includes('png') || mime.includes('png')) return 'png';
    if (fmt.includes('webp') || mime.includes('webp')) return 'webp';
    if (fmt.includes('heic') || mime.includes('heic')) return 'heic';
    if (fmt.includes('heif') || mime.includes('heif')) return 'heif';
    return 'jpg';
  };

  const isNativePickerCancel = (error: unknown) => {
    const message = String((error as any)?.message || error || '').toLowerCase();
    return (
      message.includes('cancel') ||
      message.includes('cancelled') ||
      message.includes('canceled') ||
      message.includes('user denied') ||
      message.includes('no image picked')
    );
  };

  const nativePhotoToFile = async (photo: { webPath?: string | null; path?: string | null; format?: string | null }, index = 0) => {
    const webPath = photo.webPath || photo.path;
    if (!webPath) throw new Error('Camera did not return an image path.');

    const response = await fetch(webPath);
    const blob = await response.blob();
    const mimeType = blob.type || (photo.format === 'png' ? 'image/png' : 'image/jpeg');
    const ext = getCameraFileExtension(photo.format, mimeType);
    return new File([blob], `roddo-catch-${Date.now()}-${index}.${ext}`, { type: mimeType });
  };

  const addNativeCameraPhoto = async () => {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Uri,
      quality: 86,
      width: 1800,
      allowEditing: false,
      correctOrientation: true,
      saveToGallery: false,
    });

    const file = await nativePhotoToFile(photo);
    await addPickedImages([file], isTournamentMode ? 'live_camera' : undefined);
  };

  const addNativePhotoLibraryImages = async () => {
    const openSlots = Math.max(1, MAX_IMAGES - imageFiles.length);
    const result = await Camera.pickImages({
      quality: 86,
      width: 1800,
      limit: openSlots,
    });

    const files = await Promise.all((result.photos || []).map((photo, index) => nativePhotoToFile(photo, index)));
    await addPickedImages(files, isTournamentMode ? 'uploaded_photo' : undefined);
  };

  const openPhotoSource = (source: 'camera' | 'library') => {
    setIsPhotoChooserOpen(false);

    window.setTimeout(() => {
      if (Capacitor.isNativePlatform()) {
        if (source === 'camera') {
          void addNativeCameraPhoto().catch((error) => {
            if (isNativePickerCancel(error)) return;
            console.error('[LogCatch] native camera failed', error);
            addToast('Camera had a hiccup. Try Photos instead.', 'warning');
          });
          return;
        }

        if (source === 'library') {
          void addNativePhotoLibraryImages().catch((error) => {
            if (isNativePickerCancel(error)) return;
            console.error('[LogCatch] native photo library failed', error);
            addToast('Photo library had a hiccup. Try Camera instead.', 'warning');
          });
          return;
        }
      }

      if (source === 'camera') {
        cameraInputRef.current?.click();
        return;
      }

      imageInputRef.current?.click();
    }, 80);
  };

  const handleUseCurrentLocation = () => {
    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedCoords({ lat: latitude, lon: longitude });
        if (isTournamentMode) setTournamentGpsVerified(true);
        const placeName = await resolvePlaceLabel(latitude, longitude);
        setLocation(placeName);
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationError(t('logCatch.locationError'));
        setIsLocating(false);
      },
    );
  };

  const handleLocationSelectedFromMap = async (lat: number, lon: number) => {
    setIsLocating(true);
    setSelectedCoords({ lat, lon });
    if (isTournamentMode) setTournamentGpsVerified(false);
    const placeName = await resolvePlaceLabel(lat, lon);
    setLocation(placeName);
    setIsLocating(false);
    setIsMapModalOpen(false);
  };

  const nearbyInsightCatchCount = useMemo(() => {
    if (!insightCatch || !Array.isArray(allCatchEntriesForInsight)) return 0;

    const targetTime = typeof insightCatch.timestamp === 'number' ? insightCatch.timestamp : Date.now();
    const targetLocation = String(insightCatch.location || '').trim().toLowerCase();
    const targetSpecies = String(insightCatch.species || '').trim().toLowerCase();
    const oneDayMs = 24 * 60 * 60 * 1000;

    return allCatchEntriesForInsight.filter((entry) => {
      if (!entry || entry.id === insightCatch.id) return false;
      const entryTime = typeof entry.timestamp === 'number' ? entry.timestamp : 0;
      if (!entryTime || Math.abs(targetTime - entryTime) > oneDayMs) return false;

      const sameSpecies = targetSpecies && String(entry.species || '').trim().toLowerCase() === targetSpecies;
      const entryLocation = String(entry.location || '').trim().toLowerCase();
      const sameLocation = targetLocation && entryLocation && (entryLocation === targetLocation || entryLocation.includes(targetLocation) || targetLocation.includes(entryLocation));

      return Boolean(sameSpecies || sameLocation);
    }).length;
  }, [allCatchEntriesForInsight, insightCatch]);

  const resetCatchFormAfterInsight = () => {
    catchPersistedRef.current = false;
    clearLogCatchDraft(userId);
    setSpecies('');
    setWeight('');
    setTechnique('');
    setRod('');
    setReel('');
    setLureOrBait('');
    setRodId('');
    setReelId('');
    setLureOrBaitId('');
    setRodMode('select');
    setReelMode('select');
    setLureMode('select');
    setUserEditedRod(false);
    setUserEditedReel(false);
    setUserEditedLureBait(false);
    setShareToCommunity(false);
    clearImages();
  };

  const continueAfterInsight = (route: PendingInsightRoute | null) => {
    if (isMountedRef.current) {
      setIsInsightOpen(false);
      setInsightCatch(null);
      setPendingInsightRoute(null);
      resetCatchFormAfterInsight();
    }

    if (route?.createdCommunityPostId) {
      navigateTo(View.Community, { targetId: route.createdCommunityPostId });
      return;
    }

    if (onReturnAfterSave && lastInsightCatch) {
      onReturnAfterSave({
        coords: route?.coords ?? null,
        catchEntry: lastInsightCatch,
      });
      return;
    }

    navigateTo(View.Map, {
      justLoggedCatchCoords: route?.coords
        ? { lat: route.coords.lat, lng: route.coords.lon }
        : null,
      justLoggedCatchAt: Date.now(),
    });
  };

  const handleInsightClose = () => {
    const closingCatch = insightCatch;
    markInsightClosed(closingCatch);

    if (!pendingInsightRoute) {
      setIsInsightOpen(false);
      setInsightCatch(null);
      return;
    }

    continueAfterInsight(pendingInsightRoute);
  };

  const handleReopenInsight = () => {
    const nextInsightCatch = lastInsightCatch || insightCatch;
    if (!nextInsightCatch) {
      addToast(t('logCatch.noInsightToReopen', 'No recent insight is ready yet.'), 'info');
      return;
    }

    setPendingInsightRoute(null);
    setInsightCatch(nextInsightCatch);
    setLastInsightCatch(nextInsightCatch);
    markInsightOpened(nextInsightCatch);
    setIsInsightOpen(true);
  };

  const completeTodayTask = (actionId: DailyTaskActionId) => {
    void completeDailyTaskAction(actionId)
      .then((result) => {
        if (result?.completed && !result?.alreadyCompleted) {
          const shells = Math.max(0, Math.floor(Number(result.awardedShells || 0)));
          const title = String(result.dailyTask?.title || "Today's Cast task");
          addToast(`${title} complete — +${shells} Shell${shells === 1 ? '' : 's'}`, 'success');
        }
      })
      .catch((err) => {
        console.warn('[DailyTask] completion skipped', err);
      });
  };

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();

    if (submitLockRef.current || isSubmitting || isUploadingImages) return;

    const tournamentMeasurementValue = Number(tournamentMeasurement);
    if (
      tournamentMeasurementRequired &&
      (!Number.isFinite(tournamentMeasurementValue) || tournamentMeasurementValue <= 0)
    ) {
      addToast(`Enter a valid tournament measurement in ${tournamentMeasurementUnit}.`, 'error');
      return;
    }

    if (isTournamentMode) {
      const eligibleSpecies = Array.isArray(tournamentContext?.eligibleSpecies)
        ? tournamentContext!.eligibleSpecies
            .map((item) => String(item || '').trim())
            .filter(Boolean)
        : [];
      const normalizedSpecies = String(species || '').trim().toLowerCase();
      const speciesEligible =
        !eligibleSpecies.length ||
        eligibleSpecies.some((item) => item.toLowerCase() === normalizedSpecies);

      if (normalizedSpecies && !speciesEligible) {
        addToast(
          `${species.trim()} is not eligible for this tournament. Eligible species: ${eligibleSpecies.join(', ')}.`,
          'error',
        );
        setSubmitPhase('idle');
        return;
      }

      const minQualifyingSize = Number(tournamentContext?.minQualifyingSize || 0);
      if (
        tournamentMeasurementRequired &&
        Number.isFinite(minQualifyingSize) &&
        minQualifyingSize > 0 &&
        tournamentMeasurementValue < minQualifyingSize
      ) {
        addToast(
          `Catch does not meet the ${minQualifyingSize} ${tournamentMeasurementUnit} minimum.`,
          'error',
        );
        setSubmitPhase('idle');
        return;
      }
    }

    if (isTournamentMode && tournamentRetryCatchId) {
      submitLockRef.current = true;
      setIsSubmitting(true);
      setSubmitPhase('saving');
      try {
        // A previous tournament submission attempt may already have persisted this
        // catch before the backend rejected a rule (for example, an ineligible
        // species). Merge the user's corrected form values into that same diary
        // entry before retrying so we never resubmit stale catch data.
        await setDoc(
          doc(db, 'users', userId, 'catchDiary', tournamentRetryCatchId),
          cleanObject({
            species: String(species || '').trim(),
            tournamentMeasurementValue: tournamentMeasurementRequired ? tournamentMeasurementValue : null,
            tournamentMeasurementUnit: tournamentMeasurementRequired ? tournamentMeasurementUnit : null,
            updatedAt: serverTimestamp(),
          }),
          { merge: true },
        );

        await createTournamentSubmission({
          tournamentId: tournamentContext!.tournamentId,
          catchId: tournamentRetryCatchId,
          measurementValue: tournamentMeasurementRequired ? tournamentMeasurementValue : null,
          measurementUnit: tournamentMeasurementRequired ? tournamentMeasurementUnit : null,
        });
        setTournamentRetryCatchId(null);
        trackRoddoEvent('tournament_submission_succeeded', { source: 'retry', outcome: 'submitted' });
        setSubmitPhase('success');
        setIsSubmitting(false);
        addToast('Tournament catch submitted successfully.', 'success');
        onSaveSuccess?.();

        window.setTimeout(() => {
          if (isMountedRef.current) {
            onCancel();
          }
        }, 450);
      } catch (error: any) {
        trackRoddoEvent('tournament_submission_failed', { source: 'retry', reason_code: 'submission_failed' });
        console.error('[TournamentCatch] retry failed', error);
        addToast(error?.message || 'Tournament submission retry failed.', 'error');
        setSubmitPhase('idle');
      } finally {
        submitLockRef.current = false;
        setIsSubmitting(false);
      }
      return;
    }

    if (!species || (!isTournamentMode && !weight) || (!isTournamentMode && (!date || !time)) || !location) {
      addToast(t('logCatch.formError'), 'error');
      return;
    }

    if (
      isTournamentMode &&
      tournamentContext?.locationVerification === 'required' &&
      !tournamentGpsVerified
    ) {
      addToast('Use current GPS location before submitting this tournament catch.', 'error');
      return;
    }

    const catchTimestamp = isTournamentMode ? Date.now() : buildLocalDateTimeMs(date, time);
    const catchDateValue = isTournamentMode ? getLocalDateInputValue(new Date(catchTimestamp)) : date;
    const catchTimeValue = isTournamentMode ? getLocalTimeInputValue(new Date(catchTimestamp)) : time;

    const browserTimezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || null;

    const weatherTimezone =
      String((weatherData as any)?.timezone || '').trim() || null;

    const timezone = weatherTimezone || browserTimezone || null;

    const weatherUtcOffsetSeconds = Number(
      (weatherData as any)?.utcOffsetSeconds,
    );

    const timezoneOffsetMinutes = Number.isFinite(weatherUtcOffsetSeconds)
      ? -Math.round(weatherUtcOffsetSeconds / 60)
      : new Date(catchTimestamp).getTimezoneOffset();

    const catchDate = new Date(catchTimestamp);
    const localHour = catchDate.getHours();
    const localMonth = catchDate.getMonth() + 1;

    const daylightClass =
      localHour >= 5 && localHour < 8
        ? 'dawn'
        : localHour >= 8 && localHour < 17
          ? 'day'
          : localHour >= 17 && localHour < 21
            ? 'dusk'
            : 'night';

    if (!Number.isFinite(catchTimestamp)) {
      addToast(t('logCatch.dateError', 'Please choose a valid catch date and time.'), 'error');
      return;
    }

    const maxFutureMs = Date.now() + 5 * 60 * 1000;
    if (catchTimestamp > maxFutureMs) {
      addToast(t('logCatch.futureDateError', 'Catch date cannot be in the future.'), 'error');
      setDate(getLocalDateInputValue());
      setTime(getLocalTimeInputValue());
      return;
    }

    if (!isEditMode && imageFiles.length === 0) {
      addToast(t('logCatch.imageError', 'Please add at least one photo.'), 'error');
      return;
    }

    trackRoddoEvent('catch_save_started', { source: prefilledData?.species ? 'fishiq' : isTournamentMode ? 'tournament' : 'log', share_requested: shareToCommunity, tournament_mode: isTournamentMode, edit_mode: isEditMode, photo_count: imageFiles.length });

    submitLockRef.current = true;
    setIsSubmitting(true);
    setSubmitPhase('saving');

    try {
      const tempSymbol = tempUnit === 'Fahrenheit' ? '°F' : '°C';
      const derivedWeatherString = weatherData
        ? `${weatherData.current.condition}, ${weatherData.current.temp}${tempSymbol}, ${weatherData.current.wind}`
        : undefined;
        const weatherString = weatherOverride.trim() || derivedWeatherString;
        const trimmedWaterCondition = waterCondition.trim() || undefined;
        
        const currentWeather = (weatherData as any)?.current ?? null;
        const weatherMeta = (weatherData as any) ?? null;
        
        const weatherSnapshot = cleanObject({
          conditionText: weatherString || null,
          temp: currentWeather?.temp ?? null,
          tempUnit,
          windText: currentWeather?.wind || null,
        
          feelsLike: currentWeather?.feelsLike ?? null,
          humidity: currentWeather?.humidity ?? null,
          clouds: currentWeather?.clouds ?? null,
          pressure: currentWeather?.pressure ?? null,
          precipitation: currentWeather?.precipitation ?? null,
          visibility: currentWeather?.visibility ?? null,
        
          moonPhase:
            weatherMeta?.moonPhase ||
            weatherMeta?.astronomy?.moonPhase ||
            null,
        
          sunrise:
            weatherMeta?.sunrise ||
            weatherMeta?.astronomy?.sunrise ||
            null,
        
          sunset:
            weatherMeta?.sunset ||
            weatherMeta?.astronomy?.sunset ||
            null,
        
          tideMode: weatherMeta?.tides?.mode ?? null,
        
          nextHighTide:
            currentWeather?.nextHighTide ||
            weatherMeta?.tides?.nextHigh ||
            null,
        
          nextLowTide:
            currentWeather?.nextLowTide ||
            weatherMeta?.tides?.nextLow ||
            null,
        
          observedAt:
            currentWeather?.observedAt ||
            weatherMeta?.fetchedAt ||
            null,
        
          provider:
            weatherMeta?.provider ||
            weatherMeta?.source ||
            null,
        
          timezone,
          timezoneOffsetMinutes,
        });
      const waterSnapshot = cleanObject({
        waterCondition: trimmedWaterCondition || null,
        waterClarity,
        waterLevel,
      });
      const effortMinutesNumber = Number(effortMinutes);
      const safeEffortMinutes = Number.isFinite(effortMinutesNumber)
        ? Math.max(0, Math.min(1440, Math.round(effortMinutesNumber)))
        : null;
      const safeMissedBiteCount = toSafeCount(missedBiteCount);
      const safeFollowCount = toSafeCount(followCount);
      const biteOutcome =
        observationType === 'catch' ? 'landed' :
        observationType === 'missed_bite' ? 'missed' :
        observationType === 'follow' ? 'follow' :
        observationType === 'no_catch_session' ? 'no_catch' :
        observationType === 'surface_activity' || observationType === 'baitfish_seen' || observationType === 'birds_working' || observationType === 'schooling_seen' ? 'observed_activity' :
        'unknown';
      const pilotPartnerCode = String((userProfile as any)?.fishiqPilotPartnerCode || (userProfile as any)?.fishiqPilotEnrollment?.partnerCode || '').trim() || null;
      const pilotCohortCode = String((userProfile as any)?.fishiqPilotCohortCode || (userProfile as any)?.fishiqPilotEnrollment?.cohortCode || pilotPartnerCode || 'RODDO-PILOT').trim();
      const generalizedRegionKey = buildGeneralizedRegionKey(selectedCoords, location);

      const finalRodId = rodMode === 'select' ? rodId || '' : '';
      const finalReelId = reelMode === 'select' ? reelId || '' : '';
      const finalLureBaitId = lureMode === 'select' ? lureOrBaitId || '' : '';

      const equipmentParts: string[] = [];
      if (rod?.trim()) equipmentParts.push(`Rod: ${rod.trim()}`);
      if (reel?.trim()) equipmentParts.push(`Reel: ${reel.trim()}`);
      if (lureOrBait?.trim()) {
        const label = lureOrBaitType === 'Bait' ? 'Bait' : 'Lure';
        equipmentParts.push(`${label}: ${lureOrBait.trim()}`);
      }
      if (technique?.trim()) equipmentParts.push(`Technique: ${technique.trim()}`);
      const equipmentSummary = equipmentParts.join(' • ');
      const equipmentSnapshot = cleanObject({
        rod: rod?.trim() || undefined,
        rodName: rod?.trim() || undefined,
        rodId: finalRodId || undefined,
        reel: reel?.trim() || undefined,
        reelName: reel?.trim() || undefined,
        reelId: finalReelId || undefined,
        lureOrBait: lureOrBait?.trim() || undefined,
        lureOrBaitName: lureOrBait?.trim() || undefined,
        lureOrBaitType: lureOrBait?.trim() ? lureOrBaitType : undefined,
        lureOrBaitId: finalLureBaitId || undefined,
        technique: technique?.trim() || undefined,
        source: finalRodId || finalReelId || finalLureBaitId ? 'the-hold' : 'custom',
      });

      const catchId = isEditMode
        ? String(editRaw?.id || editRaw?.catchId || '').trim()
        : (submitAttemptCatchIdRef.current || `log_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
      submitAttemptCatchIdRef.current = catchId;

      setIsUploadingImages(true);

      let uploadedImages: string[] = isEditMode ? [...existingEditImages] : [];
      try {
        const files = imageFiles.slice(0, MAX_IMAGES);
        for (let i = 0; i < files.length; i++) {
          const url = await uploadCatchImage({
            uid: userId,
            catchId,
            file: files[i],
            index: (isEditMode ? existingEditImages.length : 0) + i,
            isPublic: !isEditMode && shareToCommunity,
          });
          uploadedImages.push(url);
        }
      } finally {
        setIsUploadingImages(false);
      }

      const primary = uploadedImages[0];

      const newCatch: LogEntry = {
        id: catchId,
        species,
        weight: weight.trim() || (
          isTournamentMode && tournamentMeasurementRequired && ['lb', 'kg'].includes(tournamentMeasurementUnit)
            ? `${tournamentMeasurementValue} ${tournamentMeasurementUnit}`
            : 'Not recorded'
        ),
        date: catchDateValue,
        time: catchTimeValue,
        location,
        ...(primary ? ({ image: primary } as any) : {}),
        ...(uploadedImages.length ? ({ images: uploadedImages } as any) : {}),
        ...(uploadedImages.length ? ({ imageUrls: uploadedImages } as any) : {}),
        ...(primary ? ({ imageUrl: primary } as any) : {}),
        timestamp: catchTimestamp,

        timezone,
        timezoneOffsetMinutes,
        localDate: catchDateValue,
        localTime: catchTimeValue,
        localHour,
        localMonth,
        timeBucket: getTimeBucketLocal(catchTimestamp),
        daylightClass,

        rod,
        reel,
        lureOrBait: lureOrBait.trim() ? lureOrBait : undefined,
        lureOrBaitType: lureOrBait.trim() ? lureOrBaitType : undefined,
        technique,
        equipmentSummary: equipmentSummary || undefined,
        equipment: equipmentSummary ? equipmentSnapshot : undefined,
        weather: weatherString,
        ...(trimmedWaterCondition ? ({ waterCondition: trimmedWaterCondition, conditions: trimmedWaterCondition } as any) : {}),
        ...(selectedCoords ? ({ coords: selectedCoords } as any) : {}),
        ...(finalRodId ? ({ rodId: finalRodId } as any) : {}),
        ...(finalReelId ? ({ reelId: finalReelId } as any) : {}),
        ...(finalLureBaitId ? ({ lureOrBaitId: finalLureBaitId } as any) : {}),
        fishiqContributionConsent: contributeToFishIQ,
        fishiqContributionConsentVersion: FISHIQ_DATA_PROMISE_VERSION,
        fishiqContributionConsentAt: contributeToFishIQ ? Date.now() : null,
        fishiqPrivacyMode: contributeToFishIQ ? 'generalized_aggregate' : 'private_only',
        fishiqPrivacySummary: contributeToFishIQ
          ? 'Exact spot and identity stay private; FishIQ may use generalized aggregate observation signals.'
          : 'Private catch diary only; not included in shared FishIQ aggregate rollups.',
        researchObservationVersion: FISHIQ_RESEARCH_OBSERVATION_VERSION,
        fishiqPilotPartnerCode: pilotPartnerCode,
        fishiqPilotCohortCode: pilotCohortCode,
        generalizedRegionKey,
        generalizationMethod: selectedCoords
          ? 'rounded_latlon'
          : location
            ? 'named_place'
            : null,
        generalizationPrecision: selectedCoords ? 2 : null,
        observationType,
        activityClues,
        biteOutcome,
        missedBiteCount: safeMissedBiteCount,
        followCount: safeFollowCount,
        weatherSnapshot,
        waterSnapshot,
        waterClarity,
        waterLevel,
        catchDisposition,
        effortMinutes: safeEffortMinutes,
        ...(isTournamentMode ? ({
          tournamentEntry: true,
          tournamentLiveCapture: tournamentCaptureSource === 'live_camera',
          tournamentCaptureSource,
          tournamentSubmissionCapturePolicy: tournamentCapturePolicy,
          tournamentId: tournamentContext?.tournamentId,
          tournamentName: tournamentContext?.tournamentName,
          tournamentCode: tournamentContext?.tournamentCode || null,
          tournamentMeasurementValue: tournamentMeasurementRequired ? tournamentMeasurementValue : null,
          tournamentMeasurementUnit: tournamentMeasurementRequired ? tournamentMeasurementUnit : null,
          tournamentGpsVerified,
        } as any) : {}),
      };

      const catchData = cleanObject({
        ...newCatch,
        userId,
        createdFrom: 'log',
        ...(isEditMode ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
        ...(isEditMode
          ? {
              waterCondition: trimmedWaterCondition || null,
              conditions: trimmedWaterCondition || null,
              rod: rod?.trim() || null,
              reel: reel?.trim() || null,
              lureOrBait: lureOrBait?.trim() || null,
              lureOrBaitType: lureOrBait?.trim() ? lureOrBaitType : null,
              technique: technique?.trim() || null,
              rodId: finalRodId || null,
              reelId: finalReelId || null,
              lureOrBaitId: finalLureBaitId || null,
              equipmentSummary: equipmentSummary || null,
              equipment: equipmentSummary ? equipmentSnapshot : null,
              images: uploadedImages,
              imageUrls: uploadedImages,
              image: primary || null,
              imageUrl: primary || null,
            }
          : {
              insightAvailable: true,
              insightSeen: false,
              insightDismissed: false,
            }),
      } as any);

      const ref = doc(db, 'users', userId, 'catchDiary', newCatch.id);
      if (isEditMode) await setDoc(ref, catchData, { merge: true });
      else await setDoc(ref, catchData);

      if (!isEditMode && !isTournamentMode) {
        catchPersistedRef.current = true;
        clearLogCatchDraft(userId);
        draftRecoveredRef.current = false;
      }

      trackRoddoEvent('catch_save_succeeded', { source: prefilledData?.species ? 'fishiq' : isTournamentMode ? 'tournament' : 'log', share_requested: shareToCommunity, tournament_mode: isTournamentMode, edit_mode: isEditMode, photo_count: imageFiles.length });

      if (isEditMode) {
        onCatchAdded({ ...(editRaw || {}), ...(newCatch as any) } as LogEntry);
        setSubmitPhase('success');
        submitAttemptCatchIdRef.current = null;
        addToast('Catch updated.', 'success');
        onSaveSuccess?.();

        window.setTimeout(() => {
          if (isMountedRef.current) onCancel();
        }, 450);
        return;
      }

      if (isTournamentMode && tournamentContext?.tournamentId) {
        try {
          const submission = await createTournamentSubmission({
            tournamentId: tournamentContext.tournamentId,
            catchId: newCatch.id,
            measurementValue: tournamentMeasurementRequired ? tournamentMeasurementValue : null,
            measurementUnit: tournamentMeasurementRequired ? tournamentMeasurementUnit : null,
          });

          (newCatch as any).tournamentSubmissionId = submission.submissionId;
          (newCatch as any).tournamentSubmissionStatus = submission.status;
          trackRoddoEvent('tournament_submission_succeeded', { source: 'catch_save', outcome: String(submission.status || 'submitted') });
          await setDoc(
            ref,
            {
              tournamentSubmissionId: submission.submissionId,
              tournamentSubmissionStatus: submission.status,
            },
            { merge: true },
          );
        } catch (submissionError: any) {
          trackRoddoEvent('tournament_submission_failed', { source: 'catch_save', reason_code: 'submission_failed' });
          setTournamentRetryCatchId(newCatch.id);
          const wrapped: any = submissionError instanceof Error
            ? submissionError
            : new Error(String(submissionError?.message || submissionError || 'Tournament submission failed.'));
          wrapped.tournamentCatchSaved = true;
          throw wrapped;
        }
      }

      try {
        
        // FishIQ aggregate ingestion is unified through the catchDiary Firestore trigger.
        // Keeping one write path avoids duplicate observations, duplicate rate-limit hits,
        // and repeated progression work for the same catch.

        import('../utils/fishIQEvents').then(({ emitFishIQEvent }) => {
          emitFishIQEvent('CATCH_LOGGED', {
            userId,
            addToast,
            payload: {
              sourceId: newCatch.id,
              species,
              location,
              waterName: location,
            },
          });
        });
      } catch (e2) {
        console.warn('[FishIQ] submit observation wrapper failed (non-blocking):', e2);
      }

      let createdCommunityPostId: string | null = null;
      let communityShareFailed = false;

      if (shareToCommunity) {
        const publicLocation = buildPublicCommunityLocationLabel(location, Boolean(selectedCoords));
        const headline = `${species} – ${weight} • ${publicLocation}`;

        const communityPost = cleanObject({
          author: (userProfile as any).name,
          authorName: (userProfile as any).name || (userProfile as any).displayName || 'Angler',
          authorId: userId,
          authorAvatar: (userProfile as any).avatar || (userProfile as any).photoURL || null,
          image: primary,
          ...(uploadedImages.length ? ({ images: uploadedImages } as any) : {}),
          ...(uploadedImages.length ? ({ imageUrls: uploadedImages } as any) : {}),
          ...(primary ? ({ imageUrl: primary } as any) : {}),
          caption: headline,
          ...(headline ? ({ text: headline } as any) : {}),
          species,
          weight,
          location: publicLocation,
          publicLocation,
          locationPrivacy: 'generalized',
          exactLocationShared: false,
          technique: technique?.trim() ? technique.trim() : undefined,
          weather: weatherString,
          ...(trimmedWaterCondition ? ({ waterCondition: trimmedWaterCondition, conditions: trimmedWaterCondition } as any) : {}),
          rod: rod || undefined,
          reel: reel || undefined,
          lureOrBait: lureOrBait?.trim() ? lureOrBait : undefined,
          lureOrBaitType: lureOrBait?.trim() ? lureOrBaitType : undefined,
          rodId: finalRodId || undefined,
          reelId: finalReelId || undefined,
          lureOrBaitId: finalLureBaitId || undefined,
          equipmentSummary: equipmentSummary || undefined,
          equipment: equipmentSummary ? equipmentSnapshot : undefined,
          timestamp: Date.now(),
          isShared: true,
          shareToCommunity: true,
          bayId: inferDefaultCommunityBayId(),
          bayLabel: COMMUNITY_BAY_LABELS[inferDefaultCommunityBayId()],
          dockId: 'catch-wall',
          dockLabel: 'Catch Wall',
          roomId: 'catch-wall',
          visibilityScope: inferDefaultCommunityBayId() === 'global' ? 'global' : 'bay',
          visibility: 'public',
        });

        // One catch maps to one deterministic Baleen Bay post. Community sharing is
        // deliberately a secondary operation: the canonical Roddo Log catch is already
        // saved, so a Bay/network failure must never make the user think the catch was lost.
        const communityPostId = `catch_${newCatch.id}`;
        try {
          await createCommunityPost({
            ...communityPost,
            id: communityPostId,
          } as any);
          createdCommunityPostId = communityPostId;
          trackRoddoEvent('community_share_succeeded', { source: 'log_catch', outcome: 'shared' });

          try {
            await setDoc(
              doc(db, 'users', userId, 'catchDiary', newCatch.id),
              {
                communityPostId,
                isShared: true,
                shareToCommunity: true,
                visibility: 'public',
              },
              { merge: true },
            );
          } catch (e) {
            console.warn('Could not link communityPostId onto catchDiary doc', e);
          }
        } catch (communityError) {
          communityShareFailed = true;
          trackRoddoEvent('community_share_failed', { source: 'log_catch', reason_code: 'post_create_failed' });
          createdCommunityPostId = null;
          console.warn('[LogCatch] catch saved but Baleen Bay share failed', communityError);
        }
      }

      onCatchAdded(newCatch);
      onCatchLogged?.(selectedCoords ? { lat: selectedCoords.lat, lon: selectedCoords.lon } : null, newCatch);

      triggerHabitLoopNotifications({
        userId,
        catchEntry: newCatch,
        catchDiaryEntries,
        userProfile,
      }).catch((err: unknown) => {
  console.warn('[HabitLoop] notification trigger failed (non-blocking):', err);
});

      const successMessage = isTournamentMode
        ? 'Tournament catch saved and submitted!'
        : communityShareFailed
          ? 'Catch saved. Baleen Bay share could not finish, but your catch is safe in Roddo Log.'
          : shareToCommunity
            ? t('logCatch.saveAndShareSuccess', 'Catch saved and shared!')
            : t('logCatch.saveSuccess', 'Catch saved!');

      if (!isMountedRef.current) return;

      setSubmitPhase('success');
      submitAttemptCatchIdRef.current = null;
      addToast(successMessage, communityShareFailed ? 'warning' : 'success');
      completeTodayTask('log_catch');
      if (shareToCommunity && !communityShareFailed) completeTodayTask('share_to_bay');

      if (isTournamentMode && tournamentContext?.tournamentId) {
        // Tournament logs are opened from the tournament details flow.
        // Return through the provided cancel/back handler so we pop back to
        // that exact tournament instead of pushing a new Events route.
        setIsSubmitting(false);
        setIsSubmitPressed(false);
        onSaveSuccess?.();

        window.setTimeout(() => {
          if (isMountedRef.current) {
            onCancel();
          }
        }, 450);
        return;
      }

      // IMPORTANT: stop the old auto-route here. The insight card owns the next step.
      // This keeps LogCatchView mounted long enough for the user to actually see it.
      const routeAfterInsight: PendingInsightRoute = {
        createdCommunityPostId,
        coords: selectedCoords ? { lat: selectedCoords.lat, lon: selectedCoords.lon } : null,
      };

      setPendingInsightRoute(routeAfterInsight);
      setInsightCatch(newCatch);
      setLastInsightCatch(newCatch);

      const finishAfterInsight = () => {
        markInsightClosed(newCatch);
        continueAfterInsight(routeAfterInsight);
      };

      if (!shouldAutoOpenInsightForCatch(newCatch)) {
        continueAfterInsight(routeAfterInsight);
        onSaveSuccess?.();
        return;
      }

      markInsightOpened(newCatch);

      if (onPostLogInsight) {
        onPostLogInsight({
          catchEntry: newCatch,
          nearbyCatchCount: nearbyInsightCatchCount,
          weatherText: newCatch.weather || weatherOverride || null,
          onDone: finishAfterInsight,
        });
      } else {
        setIsInsightOpen(true);
      }

      onSaveSuccess?.();
      return;
    } catch (error: any) {
      if (error?.tournamentCatchSaved !== true) trackRoddoEvent('catch_save_failed', { source: prefilledData?.species ? 'fishiq' : isTournamentMode ? 'tournament' : 'log', reason_code: 'save_failed', tournament_mode: isTournamentMode, edit_mode: isEditMode, photo_count: imageFiles.length });
      console.error('[LogCatch] save failed', {
        error,
        code: error?.code,
        message: error?.message,
        serverResponse: error?.serverResponse,
        userId,
        authenticatedUid: auth.currentUser?.uid || null,
        imageCount: imageFiles.length,
        imageSizes: imageFiles.map((file) => file.size),
        imageTypes: imageFiles.map((file) => file.type),
        shareToCommunity,
      });
    
      const code = String(error?.code || '');
      const message = String(error?.message || '');
    
      if (isTournamentMode && error?.tournamentCatchSaved === true) {
        addToast(
          message
            ? `Catch saved to your Roddo Log. ${message} Correct the form and try again.`
            : 'Catch saved to your Roddo Log, but tournament submission failed. Correct the form and try again.',
          'error',
        );
      } else if (isTournamentMode && message) {
        addToast(message, 'error');
      } else if (
        code.includes('storage/unauthorized') ||
        code.includes('storage/unauthenticated')
      ) {
        addToast(
          'Your session could not upload this photo. Please sign out and sign back in.',
          'error',
        );
      } else if (
        message.includes('IMAGE_UPLOAD_TOO_LARGE') ||
        message.includes('IMAGE_TOO_LARGE')
      ) {
        addToast(
          'That photo is too large to process. Roddo could not reduce it safely.',
          'error',
        );
      } else {
        addToast(
          'Roddo could not upload that photo. Please try again.',
          'error',
        );
      }
      if (isMountedRef.current) {
        setSubmitPhase('idle');
      }
    } finally {
      submitLockRef.current = false;
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setIsSubmitPressed(false);
        setSubmitPhase((prev) => (prev === 'success' ? 'success' : 'idle'));
      }
    }
  };


  const isSubmitBusy = isSubmitting || isUploadingImages;
  const submitButtonLabel = tournamentRetryCatchId && isTournamentMode
    ? 'Retry Tournament Submission'
    : submitPhase === 'success'
    ? (isTournamentMode ? 'Tournament catch submitted' : t('logCatch.saveComplete', 'Catch logged'))
    : isUploadingImages
      ? t('logCatch.uploading', 'Uploading…')
      : isSubmitting
        ? t('logCatch.saving', 'Saving…')
        : (isEditMode ? 'Update Catch' : (isTournamentMode ? 'Submit Tournament Catch' : isFishIQQuickLog ? 'Save & return to Big Blue' : t('logCatch.saveCta', 'Save Catch')));
  const submitHint = tournamentRetryCatchId && isTournamentMode
    ? 'Your catch is already safe in Roddo Log. This retries only the tournament entry.'
    : submitPhase === 'success'
    ? (isTournamentMode
        ? 'Returning to the tournament…'
        : t('logCatch.saveCompleteHint', 'Taking you to the next stop…'))
    : isUploadingImages
      ? t('logCatch.uploadingHint', 'Packing your photos into the logbook.')
      : isSubmitting
        ? t('logCatch.savingHint', 'Writing your catch into the logbook.')
        : (isEditMode ? 'Save changes to this catch without creating a new entry.' : isFishIQQuickLog ? 'Save the catch, keep FishIQ validation in the background, and return to the same Big Blue flow.' : t('logCatch.saveHint', 'Save this catch to your Roddo Log.'));

  const textPrimary: React.CSSProperties = { color: ui.textPrimary };
  const textSecondary: React.CSSProperties = { color: ui.textSecondary };
  const textMuted: React.CSSProperties = { color: ui.textMuted };
  const cardStyle: React.CSSProperties = {
    background: isDarkMode
      ? '#101827'
      : 'linear-gradient(135deg, rgba(255,252,246,0.98), rgba(250,242,232,0.96))',
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDarkMode
      ? '0 6px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.025)'
      : '0 6px 16px rgba(89,65,37,0.055), inset 0 1px 0 rgba(255,255,255,0.56)',
  };
  const mutedCardStyle: React.CSSProperties = {
    background: isDarkMode
      ? '#131D2E'
      : 'linear-gradient(135deg, rgba(255,255,251,0.96), rgba(248,239,229,0.94))',
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode
      ? '0 6px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.025)'
      : '0 6px 16px rgba(89,65,37,0.06), inset 0 1px 0 rgba(255,255,255,0.50)',
  };
  const inputStyle: React.CSSProperties = {
    background: isDarkMode
      ? '#0C1423'
      : ui.inputBg,
    color: ui.textPrimary,
    border: `1px solid ${ui.borderStrong}`,
    boxShadow: isDarkMode
      ? 'inset 0 1px 0 rgba(255,255,255,0.05)'
      : 'inset 0 1px 0 rgba(255,255,255,0.60)',
  };
  const fieldClass =
    'roddo-mobile-form mt-2 block w-full min-w-0 rounded-[13px] px-3.5 py-2.5 text-[15px] font-semibold outline-none transition placeholder:opacity-100 focus:outline-none';
  const fieldLabelClass = 'block text-[12px] font-black tracking-[-0.01em]';
  const sectionPillClass = 'inline-flex items-center rounded-[12px] border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] sm:text-[12px]';
  const sectionPillStyle = (tone: 'blue' | 'mint' | 'gold' | 'hold') => ({
    background:
      tone === 'blue'
        ? theme.rewardBlueSoft
        : tone === 'mint'
          ? theme.rewardMintSoft
          : tone === 'gold'
            ? theme.rewardGoldSoft
            : isDarkMode
              ? 'rgba(115, 245, 203, 0.12)'
              : 'rgba(80, 210, 145, 0.14)',
    color:
      tone === 'blue'
        ? theme.rewardBlueText
        : tone === 'mint'
          ? theme.rewardMintText
          : tone === 'gold'
            ? theme.rewardGoldText
            : theme.rewardMintText,
    borderColor:
      tone === 'blue'
        ? theme.rewardBlue
        : tone === 'mint' || tone === 'hold'
          ? theme.rewardMint
          : theme.rewardGold,
  });

  return (
    <>
      <div ref={scrollGlowRef} className="roddo-log-desktop-lane mx-auto w-full space-y-5 pb-5 pt-3 sm:pt-4" data-roddo-tour="log-catch-page" data-roddo-tactile data-roddo-theme={isDarkMode ? 'dark' : 'light'} style={{ ...scrollGlowStyle, backgroundColor: 'transparent' }}>
        {!isTournamentMode ? (
          <div className="sticky top-0 z-30">
            <RoddoPillTabs
              items={[
                { key: 'log', label: t('logCatch.tabs.log', 'Log Catch') },
                { key: 'history', label: t('logCatch.tabs.history', 'History') },
              ]}
              activeKey="log"
              onChange={(key) => {
                if (key === 'history') navigateTo(View.CatchHistory);
              }}
              ariaLabel={t('logCatch.tabs.label', 'Roddo Log') as string}
                          />
          </div>
        ) : null}
        {isEditMode ? (
          <div className="rounded-[16px] border px-4 py-3" style={{ ...mutedCardStyle, borderColor: theme.rewardBlue }}>
            <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: theme.rewardBlueText }}>Editing catch</div>
            <div className="mt-1 text-[15px] font-black" style={textPrimary}>{species || 'Catch details'}</div>
            <p className="mt-1 text-[11px] font-semibold leading-relaxed" style={textSecondary}>
              Update the details below. Existing progression rewards are not awarded again. Existing photos stay attached unless you add more.
            </p>
          </div>
        ) : null}
        {isFishIQQuickLog ? (
          <div
            className="rounded-[18px] border px-4 py-3"
            style={{ ...mutedCardStyle, borderColor: theme.rewardBlue }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: theme.rewardBlueText }}>
                  FishIQ quick log
                </div>
                <div className="mt-1 truncate text-[16px] font-black" style={textPrimary}>
                  {species || 'Predicted fish'}
                </div>
                <p className="mt-1 text-[11px] font-semibold leading-relaxed" style={textSecondary}>
                  Species and current catch location are ready. Add the catch photo and weight, then save.
                </p>
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-black"
                style={{ borderColor: ui.borderStrong, color: ui.textPrimary }}
              >
                Back to Big Blue
              </button>
            </div>
          </div>
        ) : null}
        <style>{roddoTactilePolishStyles + `
          [data-roddo-theme="light"] input::placeholder,
          [data-roddo-theme="light"] textarea::placeholder {
            color: #8f8377;
            opacity: 1;
          }
          [data-roddo-theme="dark"] input::placeholder,
          [data-roddo-theme="dark"] textarea::placeholder {
            color: rgba(232,236,255,0.56);
            opacity: 1;
          }
          [data-roddo-theme] select option {
            color: #2b2b2b;
          }
          html.dark [data-roddo-theme] select option {
            color: #f8faff;
            background: #121321;
          }
        `}</style>
        <form onSubmit={handleSubmit} className="space-y-6">
          {isTournamentMode && tournamentContext ? (
            <div className="roddo-polish-card rounded-[22px] p-4 sm:p-5" style={{ ...cardStyle, borderColor: theme.rewardGold }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className={sectionPillClass} style={sectionPillStyle('gold')}>LIVE TOURNAMENT CATCH</div>
                  <h2 className="mt-3 text-[24px] font-black leading-tight" style={textPrimary}>{tournamentContext.tournamentName}</h2>
                  <p className="mt-1 text-[12px] font-semibold leading-relaxed" style={textSecondary}>
                    Take a new camera photo now. This catch will save to your Roddo Log and submit directly to the live tournament.
                  </p>
                </div>
                <button type="button" onClick={onCancel} className="rounded-full border px-3 py-2 text-xs font-black" style={{ borderColor: ui.borderStrong, color: ui.textPrimary }}>
                  Back to tournament
                </button>
              </div>

              {tournamentContext.tournamentCodeRequired && tournamentContext.tournamentCode ? (
                <div className="mt-4 rounded-[1.4rem] px-4 py-3" style={mutedCardStyle}>
                  <div className={fieldLabelClass} style={textMuted}>Verification code</div>
                  <div className="mt-1 text-xl font-black tracking-[0.08em]" style={textPrimary}>{tournamentContext.tournamentCode}</div>
                  <p className="mt-1 text-[11px] font-semibold" style={textSecondary}>Keep this code visible in your tournament evidence photo when the rules require it.</p>
                </div>
              ) : null}

              {tournamentMeasurementRequired ? (
                <label className="mt-4 block">
                  <span className={fieldLabelClass} style={textMuted}>Tournament measurement ({tournamentMeasurementUnit})</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={tournamentMeasurement}
                    onChange={(e) => setTournamentMeasurement(e.target.value)}
                    className={fieldClass}
                    style={inputStyle}
                    placeholder={`Enter measured ${tournamentMeasurementUnit}`}
                    required
                  />
                  {Number(tournamentContext.minQualifyingSize || 0) > 0 ? (
                    <p className="mt-1 text-[11px] font-semibold" style={textMuted}>
                      Minimum qualifying measurement: {tournamentContext.minQualifyingSize} {tournamentMeasurementUnit}
                    </p>
                  ) : null}
                </label>
              ) : null}

              {tournamentContext.locationVerification === 'required' ? (
                <div className="mt-3 rounded-xl border px-3 py-2 text-xs font-bold" style={{ borderColor: tournamentGpsVerified ? theme.rewardMint : theme.rewardGold, color: ui.textPrimary }}>
                  {tournamentGpsVerified ? '✓ Current GPS verified' : 'Current GPS is required before submission.'}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="roddo-polish-card rounded-[18px] p-4 sm:p-5" style={cardStyle}>
            <div className="mb-3">
              <h3 className="text-[18px] font-black leading-tight" style={textPrimary}>
                Catch photo
              </h3>
              <p className="mt-1 text-[12px] font-semibold leading-relaxed" style={textSecondary}>
                {isEditMode ? 'Existing photos stay attached. You can add more if needed.' : 'Add at least one photo for your log.'}
              </p>
            </div>

            <input
              type="file"
              accept="image/*"
              multiple
              ref={imageInputRef}
              onChange={(e) =>
                void addPickedImages(
                  e.target.files,
                  isTournamentMode ? 'uploaded_photo' : undefined,
                )
              }
              className="hidden"
              aria-label="Choose Catch Photos From Photo Library"
            />

            <input
              type="file"
              accept="image/*"
              ref={cameraInputRef}
              onChange={(e) =>
                void addPickedImages(
                  e.target.files,
                  isTournamentMode ? 'live_camera' : undefined,
                )
              }
              className="hidden"
              capture="environment"
              aria-label="Take Catch Photo"
            />
            {isEditMode && existingEditImages.length > 0 ? (
              <div className="mt-3 rounded-[16px] border p-3" style={mutedCardStyle}>
                <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={textMuted}>Saved photos</div>
                <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                  {existingEditImages.map((url, idx) => (
                    <button key={`${url}_${idx}`} type="button" onClick={() => openGallery(existingEditImages, idx, 'Saved catch photos')} className="shrink-0 overflow-hidden rounded-[12px]">
                      <img src={url} alt={`Saved catch ${idx + 1}`} className="h-16 w-16 object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              className="mt-3 cursor-pointer rounded-[16px] border border-dashed p-3.5"
              style={mutedCardStyle}
              onClick={() =>
                isTournamentMode && !tournamentAllowsUploads
                  ? openPhotoSource('camera')
                  : setIsPhotoChooserOpen(true)
              }
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (isTournamentMode && !tournamentAllowsUploads) {
                  addToast(
                    'Uploaded photos are disabled for this tournament. Use the live camera.',
                    'warning',
                  );
                  return;
                }
                void addPickedImages(
                  e.dataTransfer.files,
                  isTournamentMode ? 'uploaded_photo' : undefined,
                );
              }}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2" style={textPrimary}>
                    <CameraIcon className="h-5 w-5" />
                    <span className="text-[14px] font-black leading-tight">Add catch photos</span>
                  </div>
                  <div className="mt-1 text-[12px] leading-relaxed" style={textSecondary}>
                    {isTournamentMode
                      ? tournamentAllowsUploads
                        ? 'Camera or uploaded photo.'
                        : 'Live camera required.'
                      : `Camera or photo library · up to ${MAX_IMAGES}`}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {imageFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        requestClearImages();
                      }}
                      className="rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-[0.14em]"
                      style={{ background: ui.tabMutedBg, color: ui.textPrimary, border: `1px solid ${ui.borderStrong}` }}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isTournamentMode && !tournamentAllowsUploads) {
                        openPhotoSource('camera');
                        return;
                      }
                      setIsPhotoChooserOpen(true);
                    }}
                    className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.14em]"
                    style={{ background: theme.rewardBlueSoft, color: theme.rewardBlueText, border: `1px solid ${theme.rewardBlue}` }}
                  >
                    Add photo
                  </button>
                </div>
              </div>

              {imagePreviewUrls.length > 0 ? (
                imagePreviewUrls.length === 1 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery(imagePreviewUrls, 0, 'Catch photos');
                    }}
                    className="mt-4 block w-full overflow-hidden rounded-[1.25rem]"
                    title={t('profile.tapToView', 'Tap to view')}
                  >
                    <img src={imagePreviewUrls[0]} alt={t('logCatch.catchPreviewAlt', 'Catch Preview')} className="h-56 w-full object-cover" />
                  </button>
                ) : (
                  <div className="mt-4 rounded-[1.25rem] p-2" style={inputStyle}>
                    <div className="grid grid-cols-3 gap-2">
                      {imagePreviewUrls.slice(0, 6).map((url, idx) => {
                        const isLast = idx === 5 && imagePreviewUrls.length > 6;
                        return (
                          <div key={url + idx} className="relative overflow-hidden rounded-xl">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openGallery(imagePreviewUrls, idx, 'Catch photos');
                              }}
                              className="block w-full"
                              title={t('profile.tapToView', 'Tap to view')}
                            >
                              <img src={url} alt={t('logCatch.previewAlt', 'Preview {{number}}', { number: idx + 1 })} className="h-24 w-full object-cover" />
                              {isLast && (
                                <div className="absolute inset-0 grid place-items-center bg-black/45">
                                  <div className="rounded-full bg-black/60 px-3 py-1.5 text-sm font-extrabold text-white">
                                    +{imagePreviewUrls.length - 6} {t('common.more', 'more')}
                                  </div>
                                </div>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestRemovePickedImageAt(idx);
                              }}
                              className="absolute right-1 top-1 rounded-full bg-black/60 px-2 py-1 text-[11px] text-white"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-xs" style={textMuted}>
                        {imagePreviewUrls.length} photos
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGallery(imagePreviewUrls, 0, 'Catch photos');
                        }}
                        className="text-xs font-semibold"
                        style={{ color: ui.textSecondary }}
                      >
                        View all
                      </button>
                    </div>
                  </div>
                )
              ) : null}

              {isUploadingImages ? (
                <div className="mt-3 text-xs" style={textMuted}>
                  {t('community.uploading', 'Uploading photos…')}
                </div>
              ) : null}
            </div>
            {(isScanningFish || scanResult) && (
              <div className="mt-4 rounded-[1.6rem] p-4" style={{ ...mutedCardStyle, borderColor: theme.rewardGold }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={fieldLabelClass} style={textMuted}>FishIQ Scan</div>
                    <div className="mt-1 text-lg font-black" style={textPrimary}>
                      {isScanningFish ? 'Reading the fish photo…' : scanResult?.species || 'Possible fish match'}
                    </div>
                    <p className="mt-1 text-[12px] leading-relaxed" style={textSecondary}>
                      Photo-based estimate only. Double-check species, length, and weight before saving your catch.
                    </p>
                  </div>
                  <div className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]" style={{ background: theme.rewardGoldSoft, color: theme.rewardGoldText }}>
                    Phase 1
                  </div>
                </div>

                {scanImageUrl && (
                  <button
                    type="button"
                    onClick={() => openGallery([scanImageUrl], 0, 'FishIQ Scan photo')}
                    className="mt-3 block w-full overflow-hidden rounded-[1.25rem]"
                    title="View scan photo"
                  >
                    <img src={scanImageUrl} alt="FishIQ Scan" className="h-32 w-full object-cover" />
                  </button>
                )}

                {scanResult && !isScanningFish && (
                  <div className="mt-4 grid gap-2 text-sm">
                    <div className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2" style={inputStyle}>
                      <span className="font-black">Species</span>
                      <span className="text-right font-extrabold">{scanResult.species || 'Unknown'} · {formatConfidence(scanResult.confidence)}</span>
                    </div>
                    {isFishIQQuickLog && prefilledData?.species && scanResult.species &&
                    scanResult.species.trim().toLowerCase() !== prefilledData.species.trim().toLowerCase() ? (
                      <div className="rounded-2xl px-3 py-2 text-[12px] font-semibold leading-relaxed" style={{ ...inputStyle, color: ui.textSecondary }}>
                        FishIQ selected <strong>{prefilledData.species}</strong>. A photo-scan disagreement will not replace it automatically.
                      </div>
                    ) : null}
                    {typeof scanResult.lengthInches === 'number' && (
                      <div className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2" style={inputStyle}>
                        <span className="font-black">Length</span>
                        <span className="text-right font-extrabold">~{scanResult.lengthInches.toFixed(1)} in · {formatConfidence(scanResult.lengthConfidence)}</span>
                      </div>
                    )}
                    {typeof scanResult.weightLbs === 'number' && (
                      <div className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2" style={inputStyle}>
                        <span className="font-black">Weight</span>
                        <span className="text-right font-extrabold">~{scanResult.weightLbs.toFixed(scanResult.weightLbs >= 10 ? 1 : 2)} lb · {formatConfidence(scanResult.weightConfidence)}</span>
                      </div>
                    )}
                    {scanResult.notes && (
                      <div className="rounded-2xl px-3 py-2 text-[12px] leading-relaxed" style={{ ...inputStyle, color: ui.textSecondary }}>
                        {scanResult.notes}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleUseFishIQScanData}
                      className="mt-2 w-full rounded-full px-4 py-3 text-sm font-black uppercase tracking-[0.16em]"
                      style={{ background: theme.rewardBlueSoft, color: theme.rewardBlueText, border: `2px solid ${theme.rewardBlue}` }}
                    >
                      Use this data
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>

          <div className="roddo-polish-card rounded-[18px] p-4 sm:p-5" style={cardStyle}>
            <div className="mb-4">
              <h3 className="text-[18px] font-black leading-tight" style={textPrimary}>
                Catch details
              </h3>
            </div>

            <div className="grid gap-3">
              <div>
                <label htmlFor="species" className={fieldLabelClass} style={textMuted}>
                  Species
                </label>
                <input
                  type="text"
                  id="species"
                  value={species}
                  onChange={(e) => setSpecies(e.target.value)}
                  className={fieldClass}
                  style={inputStyle}
                  placeholder={t('logCatch.speciesPlaceholder', 'e.g., Largemouth Bass')}
                  required
                />
              </div>

              <div>
                <label htmlFor="weight" className={fieldLabelClass} style={textMuted}>
                  Weight
                </label>
                <input
                  type="text"
                  id="weight"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className={fieldClass}
                  style={inputStyle}
                  placeholder={isTournamentMode ? 'Optional unless tournament rules require weight' : t('logCatch.weightPlaceholder', 'e.g., 5.2 lbs')}
                  required={!isTournamentMode}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="date" className={fieldLabelClass} style={textMuted}>
                    {t('logCatch.date', 'Date')}
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={isTournamentMode ? getLocalDateInputValue() : date}
                    max={getLocalDateInputValue()}
                    disabled={isTournamentMode}
                    onChange={(e) => setDate(e.target.value)}
                    className={`${fieldClass} min-w-0 appearance-none`}
                    style={inputStyle}
                    required
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="time" className={fieldLabelClass} style={textMuted}>
                    {t('logCatch.time', 'Time')}
                  </label>
                  <input
                    type="time"
                    id="time"
                    value={isTournamentMode ? getLocalTimeInputValue() : time}
                    disabled={isTournamentMode}
                    onChange={(e) => setTime(e.target.value)}
                    className={`${fieldClass} min-w-0 appearance-none`}
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div className="relative">
                <label htmlFor="location" className={fieldLabelClass} style={textMuted}>
                  Location
                </label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className={`${fieldClass} pr-28`}
                  style={inputStyle}
                  placeholder={t('logCatch.locationPlaceholder', 'Pomona, California')}
                  autoComplete="off"
                  required
                />
                <div className="absolute right-2 top-8 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    className="rounded-full p-2"
                    style={{ color: theme.rewardMintText }}
                    title={t('logCatch.useCurrentLocation', 'Use current location')}
                  >
                    {isLocating ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2" style={{ borderColor: theme.rewardGold }} />
                    ) : (
                      <CrosshairIcon className="h-5 w-5" />
                    )}
                  </button>
                  {!isTournamentMode ? (
                    <button
                      type="button"
                      onClick={() => setIsMapModalOpen(true)}
                      className="rounded-full p-2"
                      style={{ color: ui.textSecondary }}
                      title={t('logCatch.selectOnMap', 'Select on map')}
                    >
                      <MapPinIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
                {locationError && (
                  <p className="mt-1 text-xs" style={{ color: '#d94b4b' }}>
                    {locationError}
                  </p>
                )}

                {!isTournamentMode && selectedCoords ? (
                  <div className="[&>div]:!h-[138px]">
                    <CatchLocationMapPreview
                      lat={selectedCoords.lat}
                      lon={selectedCoords.lon}
                      label={location}
                      isDarkMode={isDarkMode}
                      onOpen={() => setIsMapModalOpen(true)}
                      actionLabel="Adjust spot"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {isFishIQQuickLog ? (
            <button
              type="button"
              onClick={() => setShowQuickExtras((value) => !value)}
              className="flex w-full items-center justify-between gap-3 rounded-[16px] border px-4 py-3 text-left"
              style={mutedCardStyle}
              aria-expanded={showQuickExtras}
            >
              <span className="min-w-0">
                <span className="block text-[13px] font-black" style={textPrimary}>
                  {showQuickExtras ? 'Hide extra details' : 'Add conditions & gear'}
                </span>
                <span className="mt-0.5 block text-[11px] font-semibold" style={textMuted}>
                  Optional — weather, water notes, FishIQ observations, rod, reel, lure, and technique
                </span>
              </span>
              <span className="text-lg font-black opacity-50">{showQuickExtras ? '−' : '+'}</span>
            </button>
          ) : null}

          {(!isFishIQQuickLog || showQuickExtras) ? (
            <>
          <div className="roddo-polish-card rounded-[18px] p-4 sm:p-5" style={cardStyle}>
            <div className="mb-4">
              <h3 className="text-[18px] font-black leading-tight" style={textPrimary}>
                Conditions
              </h3>
              <p className="mt-1 text-[12px] font-semibold" style={textSecondary}>
                Weather is prefilled when available.
              </p>
            </div>

            <div className="grid gap-4">
              <div>
                <label htmlFor="weatherOverride" className={fieldLabelClass} style={textMuted}>
                  Weather
                </label>
                <input
                  type="text"
                  id="weatherOverride"
                  value={weatherOverride}
                  onChange={(e) => setWeatherOverride(e.target.value)}
                  className={fieldClass}
                  style={inputStyle}
                  placeholder="Sunny, 68°F, light wind"
                />
              </div>

              <div>
                <label htmlFor="waterCondition" className={fieldLabelClass} style={textMuted}>
                  Water conditions
                </label>
                <textarea
                  id="waterCondition"
                  value={waterCondition}
                  onChange={(e) => setWaterCondition(e.target.value)}
                  className={`${fieldClass} min-h-[84px] resize-y`}
                  style={inputStyle}
                  placeholder="Clear water, light chop, incoming tide"
                />
              </div>

              <div className="border-t pt-3" style={{ borderColor: ui.border }}>
                <button
                  type="button"
                  onClick={() => setShowFieldNotes((value) => !value)}
                  className="flex w-full items-center justify-between gap-3 py-1 text-left"
                  aria-expanded={showFieldNotes}
                >
                  <span>
                    <span className="block text-[13px] font-black" style={textPrimary}>More field notes</span>
                    <span className="mt-0.5 block text-[11px] font-semibold" style={textMuted}>
                      Optional FishIQ observations and water details
                    </span>
                  </span>
                  <span className="text-lg font-black opacity-50">{showFieldNotes ? '−' : '+'}</span>
                </button>

                {showFieldNotes ? (
                  <div className="mt-4 grid gap-4">
                    <label>
                      <span className={fieldLabelClass} style={textMuted}>Observation type</span>
                      <select
                        value={observationType}
                        onChange={(e) => setObservationType(e.target.value as FishIQObservationType)}
                        className={fieldClass}
                        style={inputStyle}
                      >
                        <option value="catch">Catch landed</option>
                        <option value="missed_bite">Missed bite</option>
                        <option value="follow">Follow</option>
                        <option value="surface_activity">Surface activity</option>
                        <option value="baitfish_seen">Baitfish seen</option>
                        <option value="birds_working">Birds working</option>
                        <option value="schooling_seen">Schooling seen</option>
                        <option value="no_catch_session">No-catch session</option>
                      </select>
                    </label>

                    <div>
                      <span className={fieldLabelClass} style={textMuted}>Field signs</span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {FISHIQ_ACTIVITY_CLUE_OPTIONS.map((opt) => {
                          const active = activityClues.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => toggleActivityClue(opt.value)}
                              className="rounded-full px-2.5 py-1.5 text-[11px] font-black transition"
                              style={{
                                border: `1px solid ${active ? 'rgba(118, 242, 197, 0.72)' : ui.borderStrong}`,
                                background: active ? 'rgba(118, 242, 197, 0.14)' : 'transparent',
                                color: ui.textPrimary,
                              }}
                            >
                              {active ? '✓ ' : ''}{opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Missed bites</span>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          inputMode="numeric"
                          value={missedBiteCount}
                          onChange={(e) => setMissedBiteCount(e.target.value)}
                          className={fieldClass}
                          style={inputStyle}
                          placeholder="Optional"
                        />
                      </label>
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Follows</span>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          inputMode="numeric"
                          value={followCount}
                          onChange={(e) => setFollowCount(e.target.value)}
                          className={fieldClass}
                          style={inputStyle}
                          placeholder="Optional"
                        />
                      </label>
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Water clarity</span>
                        <select value={waterClarity} onChange={(e) => setWaterClarity(e.target.value as any)} className={fieldClass} style={inputStyle}>
                          <option value="unknown">Not sure</option>
                          <option value="clear">Clear</option>
                          <option value="stained">Stained</option>
                          <option value="muddy">Muddy</option>
                        </select>
                      </label>
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Water level</span>
                        <select value={waterLevel} onChange={(e) => setWaterLevel(e.target.value as any)} className={fieldClass} style={inputStyle}>
                          <option value="unknown">Not sure</option>
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                        </select>
                      </label>
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Catch outcome</span>
                        <select value={catchDisposition} onChange={(e) => setCatchDisposition(e.target.value as any)} className={fieldClass} style={inputStyle}>
                          <option value="unknown">Prefer not to say</option>
                          <option value="released">Released</option>
                          <option value="harvested">Harvested</option>
                        </select>
                      </label>
                      <label>
                        <span className={fieldLabelClass} style={textMuted}>Effort minutes</span>
                        <input
                          type="number"
                          min="0"
                          max="1440"
                          inputMode="numeric"
                          value={effortMinutes}
                          onChange={(e) => setEffortMinutes(e.target.value)}
                          className={fieldClass}
                          style={inputStyle}
                          placeholder="45"
                        />
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="roddo-polish-card rounded-[18px] p-4 sm:p-5" style={cardStyle}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-[18px] font-black leading-tight" style={textPrimary}>Gear</h3>
                <p className="mt-1 text-[12px] font-semibold" style={textSecondary}>
                  Use your equipped setup or make a quick change.
                </p>
              </div>
              <span
                className="rounded-full px-2.5 py-1 text-[10px] font-black"
                style={{ background: theme.rewardMintSoft, color: theme.rewardMintText }}
              >
                {gearItems?.length ? 'Loaded from The Hold' : 'Custom'}
              </span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={fieldLabelClass} style={textMuted}>Rod</label>
                <select
                  className={fieldClass}
                  style={inputStyle}
                  value={rodMode === 'custom' ? CUSTOM_VALUE : rodId}
                  onChange={(e) => onSelectRod(e.target.value)}
                >
                  <option value="">{rods.length ? 'Select a saved rod…' : 'No rods saved yet'}</option>
                  {rods.map((item: any) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                  <option value={CUSTOM_VALUE}>Custom rod…</option>
                </select>
                {rodMode === 'custom' ? (
                  <input
                    type="text"
                    value={rod}
                    onChange={(e) => {
                      setRod(e.target.value);
                      setUserEditedRod(true);
                    }}
                    className={fieldClass}
                    style={inputStyle}
                    placeholder="Type your rod…"
                  />
                ) : null}
              </div>

              <div>
                <label className={fieldLabelClass} style={textMuted}>Reel</label>
                <select
                  className={fieldClass}
                  style={inputStyle}
                  value={reelMode === 'custom' ? CUSTOM_VALUE : reelId}
                  onChange={(e) => onSelectReel(e.target.value)}
                >
                  <option value="">{reels.length ? 'Select a saved reel…' : 'No reels saved yet'}</option>
                  {reels.map((item: any) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                  <option value={CUSTOM_VALUE}>Custom reel…</option>
                </select>
                {reelMode === 'custom' ? (
                  <input
                    type="text"
                    value={reel}
                    onChange={(e) => {
                      setReel(e.target.value);
                      setUserEditedReel(true);
                    }}
                    className={fieldClass}
                    style={inputStyle}
                    placeholder="Type your reel…"
                  />
                ) : null}
              </div>

              <div>
                <label className={fieldLabelClass} style={textMuted}>
                  {t('logCatch.lureBaitLabel', 'Lure / bait')}
                </label>
                <select
                  className={fieldClass}
                  style={inputStyle}
                  value={lureMode === 'custom' ? CUSTOM_VALUE : lureOrBaitId}
                  onChange={(e) => onSelectLureBait(e.target.value)}
                >
                  <option value="">{luresBaits.length ? 'Select saved lure/bait…' : 'No lures or baits saved yet'}</option>
                  {luresBaits.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.category ? `(${item.category})` : ''}
                    </option>
                  ))}
                  <option value={CUSTOM_VALUE}>Custom lure/bait…</option>
                </select>

                {lureMode === 'custom' ? (
                  <div className="mt-2 grid gap-2">
                    <input
                      type="text"
                      value={lureOrBait}
                      onChange={(e) => {
                        setLureOrBait(e.target.value);
                        setUserEditedLureBait(true);
                      }}
                      className={fieldClass}
                      style={inputStyle}
                      placeholder={t('logCatch.lureBaitPlaceholder', 'Topwater frog or nightcrawler')}
                    />
                    <div className="flex items-center gap-4 px-1 text-xs font-bold" style={textSecondary}>
                      <label className="flex items-center gap-2">
                        <input type="radio" name="lureOrBaitType" value="Lure" checked={lureOrBaitType === 'Lure'} onChange={() => setLureOrBaitType('Lure')} />
                        Lure
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="radio" name="lureOrBaitType" value="Bait" checked={lureOrBaitType === 'Bait'} onChange={() => setLureOrBaitType('Bait')} />
                        Bait
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label htmlFor="technique" className={fieldLabelClass} style={textMuted}>
                  {t('logCatch.techniqueLabel', 'Technique')}
                </label>
                <input
                  type="text"
                  id="technique"
                  value={technique}
                  onChange={(e) => setTechnique(e.target.value)}
                  className={fieldClass}
                  style={inputStyle}
                  placeholder={t('logCatch.techniquePlaceholder', 'Drop shot')}
                />
              </div>
            </div>
          </div>

            </>
          ) : null}

          {!isTournamentMode && !isEditMode ? (
            <div className="roddo-polish-card rounded-[18px] p-4 sm:p-5" style={cardStyle}>
              <div className="grid gap-3">
                <div
                  className="flex items-center justify-between gap-4 rounded-[16px] px-4 py-3"
                  style={mutedCardStyle}
                >
                  <div className="min-w-0">
                    <p className="text-[15px] font-black" style={textPrimary}>
                      Share to Baleen Bay
                    </p>
                    <p className="mt-1 text-[12px] font-semibold leading-relaxed" style={textMuted}>
                      Optional public post. Your exact catch spot stays private; only a general area is shown.
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={shareToCommunity}
                    aria-label={shareToCommunity ? 'Disable community sharing' : 'Enable community sharing'}
                    onClick={() => setShareToCommunity((v) => !v)}
                    className="relative inline-flex h-9 w-[68px] shrink-0 items-center rounded-full p-1 transition-all duration-200"
                    style={{
                      background: shareToCommunity
                        ? `linear-gradient(180deg, ${theme.rewardMint}, #57d39f)`
                        : isDarkMode
                          ? '#0C1423'
                          : 'linear-gradient(180deg, rgba(229,220,209,0.96), rgba(213,203,191,0.98))',
                      border: `1px solid ${shareToCommunity ? 'rgba(118, 242, 197, 0.72)' : ui.borderStrong}`,
                      boxShadow: shareToCommunity
                        ? '0 10px 20px rgba(63,182,143,0.22), inset 0 1px 0 rgba(255,255,255,0.2)'
                        : `${ui.shadow}, ${ui.insetHighlight}`,
                    }}
                  >
                    <span
                      className="pointer-events-none absolute left-1 top-1 h-7 w-7 rounded-full transition-transform duration-200"
                      style={{
                        transform: shareToCommunity ? 'translateX(28px)' : 'translateX(0)',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(244,239,233,0.96))',
                        boxShadow: '0 8px 16px rgba(15,23,42,0.2)',
                      }}
                    />
                    <span className="sr-only">Share to Baleen Bay</span>
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="pt-1">
            {isTournamentMode ? (
              <div className="mb-4 rounded-[14px] px-4 py-3" style={mutedCardStyle}>
                <div className={sectionPillClass} style={sectionPillStyle('gold')}>
                  Tournament entry
                </div>
                <p className="mt-2 text-[12px] font-semibold leading-relaxed" style={textSecondary}>
                  Submit this catch to {tournamentContext?.tournamentName || 'the live tournament'}.
                </p>
              </div>
            ) : null}

            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <button
                  type="submit"
                  disabled={isSubmitBusy}
                  aria-busy={isSubmitBusy}
                  onPointerDown={() => {
                    if (!isSubmitBusy) setIsSubmitPressed(true);
                  }}
                  onPointerUp={() => setIsSubmitPressed(false)}
                  onPointerLeave={() => setIsSubmitPressed(false)}
                  onBlur={() => setIsSubmitPressed(false)}
                  className="relative w-full overflow-hidden rounded-[14px] px-8 py-3.5 text-[15px] font-black uppercase tracking-[0.16em] transition duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-[2px] disabled:cursor-not-allowed disabled:opacity-100"
                  style={{
                    border: `1px solid ${submitPhase === 'success' ? theme.rewardMint : '#E5AA22'}`,
                    background: submitPhase === 'success'
                      ? `linear-gradient(180deg, ${theme.rewardMint}, #34d399)`
                      : 'linear-gradient(180deg, #FFD96A 0%, #FFC94A 52%, #F6B936 100%)',
                    boxShadow: submitPhase === 'success'
                      ? '0 14px 28px rgba(52,211,153,0.28), 0 0 0 1px rgba(255,255,255,0.16) inset, 0 0 22px rgba(134,239,172,0.22)'
                      : isSubmitBusy
                        ? '0 10px 20px rgba(181,126,24,0.20), 0 0 0 1px rgba(255,255,255,0.18) inset'
                        : '0 12px 26px rgba(181,126,24,0.28), 0 0 0 1px rgba(255,255,255,0.22) inset',
                    color: '#2F2410',
                    transform: `${isSubmitPressed && !isSubmitBusy ? 'scale(0.985)' : 'scale(1)'} ${submitPhase === 'success' ? 'scale(1.02)' : ''}`.trim(),
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-3 top-1 h-[42%] rounded-[10px]"
                    style={{
                      background: submitPhase === 'success'
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.34), rgba(255,255,255,0.08))'
                        : 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.06))',
                    }}
                  />
                  <span className="relative z-[1] inline-flex items-center justify-center gap-2">
                    {submitPhase === 'success' ? <span aria-hidden="true">✓</span> : null}
                    <span>{submitButtonLabel}</span>
                  </span>
                </button>

                <div
                  className="mt-2 min-h-[20px] text-center text-[11px] font-semibold tracking-[0.04em]"
                  style={submitPhase === 'success' ? { color: theme.rewardMintText } : textMuted}
                >
                  {submitHint}
                </div>

                {!isEditMode ? (
                  <div className="mt-2 flex justify-center">
                    <button
                      type="button"
                      disabled={isSubmitBusy}
                      onClick={() => setClearCatchConfirmOpen(true)}
                      className="rounded-full border px-3 py-2 text-[10px] font-extrabold uppercase tracking-[0.12em] transition hover:-translate-y-0.5 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                      style={{
                        borderColor: ui.borderStrong,
                        background: isDarkMode ? 'rgba(15,23,42,0.78)' : 'rgba(255,255,255,0.72)',
                        color: ui.textSecondary,
                      }}
                    >
                      Clear Catch
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {lastInsightCatch && !isInsightOpen && !isTournamentMode && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={handleReopenInsight}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] transition hover:-translate-y-0.5 active:translate-y-[1px]"
                style={{
                  borderColor: ui.borderStrong,
                  background: isDarkMode
                    ? '#101827'
                    : 'linear-gradient(180deg, rgba(255,252,247,0.92), rgba(246,238,229,0.9))',
                  color: ui.textSecondary,
                  boxShadow: ui.shadow,
                }}
                aria-label="Reopen latest FishIQ insight"
              >
                <span
                  aria-hidden="true"
                  className="grid h-4 w-4 place-items-center rounded-[0.35rem] border text-[10px]"
                  style={{
                    borderColor: theme.rewardMint,
                    background: isDarkMode ? 'rgba(52,211,153,0.14)' : 'rgba(16,185,129,0.12)',
                    color: theme.rewardMintText,
                  }}
                >
                  ✓
                </span>
                <span>{t('logCatch.reopenInsight', 'View FishIQ Insight')}</span>
              </button>
            </div>
          )}
        </form>
      </div>

      <RoddoInsightCard
        open={isInsightOpen}
        catchEntry={insightCatch}
        nearbyCatchCount={nearbyInsightCatchCount}
        weatherText={insightCatch?.weather || weatherOverride || null}
        onClose={handleInsightClose}
        onDisablePostCatchInsight={onDisablePostCatchInsight}
      />

      {isMapModalOpen && (
        <LocationPickerModal
          coords={selectedCoords ?? coords}
          isDarkMode={isDarkMode}
          onClose={() => setIsMapModalOpen(false)}
          onLocationSelect={handleLocationSelectedFromMap}
        />
      )}


      {!isTournamentMode ? (
        <RoddoConfirmDialog
          open={isPhotoChooserOpen}
          title="Add photo"
          description="Choose how you want to add catch photos."
          cancelLabel={t('common.cancel', 'Cancel') as string}
          tone="info"
          icon="📷"
          isDarkMode={isDarkMode}
          actions={[
            {
              label: 'Camera',
              description: 'Take a new catch shot',
              icon: '📷',
              tone: 'primary',
              onClick: () => openPhotoSource('camera'),
            },
            ...((!isTournamentMode || tournamentAllowsUploads)
              ? [
                  {
                    label: 'Photos',
                    description: 'Pick from camera roll',
                    icon: '🖼️',
                    tone: 'neutral' as const,
                    onClick: () => openPhotoSource('library'),
                  },
                ]
              : []),
          ]}
          onCancel={() => setIsPhotoChooserOpen(false)}
        />
      ) : null}

      {gallery.open && gallery.images.length > 0 && (
        <GalleryLightbox images={gallery.images} startIndex={gallery.startIndex} title={gallery.title} onClose={closeGallery} />
      )}

      <RoddoConfirmDialog
        open={clearCatchConfirmOpen}
        title="Clear this catch?"
        description={tournamentRetryCatchId
          ? 'This resets the current form and starts a fresh catch entry. The previously saved failed catch may still appear in Roddo Log history.'
          : 'This clears the current catch information and photos so you can start the entry over. Your selected location and tournament stay in place.'}
        confirmLabel="Clear Catch"
        cancelLabel={t('common.cancel', 'Cancel') as string}
        tone="warning"
        isDarkMode={isDarkMode}
        onConfirm={clearWorkingCatch}
        onCancel={() => setClearCatchConfirmOpen(false)}
      />

      <RoddoConfirmDialog
        open={!!photoConfirm}
        title={photoConfirm?.mode === 'all' ? 'Clear selected photos?' : 'Remove this photo?'}
        description={photoConfirm?.mode === 'all' ? t('logCatch.removePhotosConfirm', 'This removes the selected photos from this draft catch log.') : t('logCatch.removePhotoConfirm', 'This removes the photo from this draft catch log.')}
        confirmLabel={photoConfirm?.mode === 'all' ? 'Clear photos' : 'Remove'}
        cancelLabel={t('common.cancel', 'Cancel') as string}
        tone="warning"
        isDarkMode={isDarkMode}
        onConfirm={confirmPhotoRemoval}
        onCancel={() => setPhotoConfirm(null)}
      />
    </>
  );
};

const LocationPickerModal: React.FC<{
  onClose: () => void;
  onLocationSelect: (lat: number, lon: number) => void;
  coords: { lat: number; lon: number } | null;
  isDarkMode: boolean;
}> = ({ onClose, onLocationSelect, coords, isDarkMode }) => {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(
    coords ? { lat: coords.lat, lng: coords.lon } : null,
  );

  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current && typeof L !== 'undefined') {
      const initialCenter = coords ? [coords.lat, coords.lon] : [40.7128, -74.006];
      const map = L.map(mapRef.current).setView(initialCenter, 13);
      mapInstanceRef.current = map;

      const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles courtesy of <a href="https://www.hotosm.org/">HOT</a>',
        maxZoom: 18,
      }).addTo(map);

      window.setTimeout(() => {
        const tilePane = map.getPane?.('tilePane') as HTMLElement | undefined;
        if (tilePane) {
          tilePane.style.filter = isDarkMode
            ? 'brightness(0.50) contrast(0.96) saturate(0.72)'
            : 'none';
          tilePane.style.transition = 'filter 180ms ease';
          tilePane.style.willChange = isDarkMode ? 'filter' : 'auto';
        }

        const tileContainer = tileLayer.getContainer?.() as HTMLElement | undefined;
        if (tileContainer) {
          tileContainer.style.filter = 'none';
          tileContainer.style.transition = 'none';
        }
      }, 0);

      markerRef.current = L.marker(initialCenter, {
        draggable: true,
        autoPan: true,
        riseOnHover: true,
        icon: createLocationPickerPinIcon(isDarkMode),
      }).addTo(map);
      setSelectedCoords(markerRef.current.getLatLng());

      markerRef.current.on('dragend', (event: any) => {
        setSelectedCoords(event.target.getLatLng());
      });

      map.on('click', (e: any) => {
        markerRef.current.setLatLng(e.latlng);
        setSelectedCoords(e.latlng);
      });

      const settleTimer = window.setTimeout(() => {
        try { map.invalidateSize(false); } catch {}
      }, 100);

      return () => {
        window.clearTimeout(settleTimer);
        markerRef.current = null;
        mapInstanceRef.current = null;
        try { map.remove(); } catch {}
      };
    }
  }, [coords, isDarkMode]);

  const theme = getRoddoTheme(isDarkMode);
  const ui = getRoddoUiTokens(isDarkMode);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-3 pb-[max(14px,env(safe-area-inset-bottom))] pt-[max(14px,env(safe-area-inset-top))] backdrop-blur-sm sm:px-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[440px] flex-col overflow-hidden rounded-[20px] border shadow-2xl"
        style={{
          height: 'min(560px, calc(var(--roddo-visual-vh, 100dvh) - max(28px, env(safe-area-inset-top)) - max(28px, env(safe-area-inset-bottom))))',
          maxHeight: 'calc(var(--roddo-visual-vh, 100dvh) - max(28px, env(safe-area-inset-top)) - max(28px, env(safe-area-inset-bottom)))',
          background: isDarkMode
            ? '#0E1020'
            : 'linear-gradient(180deg, rgba(255,251,245,0.99), rgba(247,238,227,0.99))',
          borderColor: theme.borderStrong,
          boxShadow: theme.shadowStrong,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: ui.textMuted }}>
              Catch location
            </div>
            <div className="mt-0.5 text-[14px] font-black" style={{ color: ui.textPrimary }}>
              Fine-tune your spot
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-base font-black"
            style={{ background: ui.tabMutedBg, borderColor: ui.borderStrong, color: ui.textPrimary }}
            aria-label="Close location picker"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 px-3">
          <div
            ref={mapRef}
            className="h-full w-full overflow-hidden rounded-[18px] border"
            style={{ borderColor: theme.borderStrong, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }}
          />
        </div>

        <div className="shrink-0 space-y-2 px-3 pb-3 pt-3">
          <p className="px-1 text-center text-[11px] font-semibold leading-relaxed" style={{ color: ui.textSecondary }}>
            {t('logCatch.mapModalPrompt', 'Drag the pin or tap the map to fine-tune your catch spot.')}
          </p>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <button
              type="button"
              onClick={() => {
                if (selectedCoords) onLocationSelect(selectedCoords.lat, selectedCoords.lng);
              }}
              className="min-h-11 rounded-[14px] px-4 py-2.5 text-[13px] font-black"
              style={{ background: theme.rewardGold, color: '#33210a' }}
            >
              {t('logCatch.confirmLocation', 'Use this location')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 rounded-[14px] border px-4 py-2.5 text-[12px] font-black"
              style={{ background: ui.tabMutedBg, borderColor: ui.borderStrong, color: ui.textPrimary }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogCatchView;